import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControl,
    Autocomplete,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    AlertTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    LinearProgress,
    styled,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Button } from '@mui/material';
import { Link } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { DataGridPro } from '@mui/x-data-grid-pro';
import ExcelViewerDialog from './GLSync/ExcelViewerDialog';
import ViewDataDialog from './GLSync/ViewDataDialog';
import RefTemplateMappingsEditorDrawer from './GLSync/RefTemplateMappingsEditorDrawer';
import {
    buildAdditionalAttributesFromCombinationRow,
    buildAdditionalAttributesFromHeaderIndexRow,
    buildAdditionalAttributesFromTemplateRow,
    buildGlAccountRowForS3NativeKeys,
    compactGlAccountRowForUpload,
    getTemplateMappedCanonicalUpperSet,
    getTemplateMappedExcelHeadersLower,
} from './glChartOfAccountUploadExtras';
import { useUserRights } from '../../context/UserRightsContext';
import { formatDate, formatDateTime, initializeFormats, getFormatConfig } from '../../utils/global';
import { splitSurrogateKeyTokens } from '../../utils/surrogateKeyTokens';
import { getToken, getTenantId } from '../../utils/auth';
import { AuditLoggers } from '../../utils/auditLogger';
import { useToast } from '../hooks/useToast';
import Toast from '../common/Toast';
import * as XLSX from 'xlsx';
import { toChartOfAccountGlSystem } from '../../constants/glSyncDocumentTypes';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
    harmonatePageSx,
    harmonatePageTitleSx,
    harmonateCardSx,
    harmonateDataGridSx,
    harmonateProceedButtonSx,
    harmonateUploadOutlinedSx,
    harmonateCancelButtonSx,
    harmonateAbortButtonSx,
    harmonateDialogPaperProps,
    harmonateDialogTitleSx,
    harmonateDialogContentSx,
    harmonateDialogCloseIconSx,
    harmonateFormFieldsSx,
    harmonatePageSearchFieldSx,
    harmonatePageSearchIconSx,
} from '../../theme/harmonateStyles';

const UploadArea = styled(Box)(() => ({
    border: `2px dashed ${ht.line}`,
    borderRadius: ht.cardRadius,
    padding: '30px 20px',
    textAlign: 'center',
    backgroundColor: ht.listBg,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: ht.green,
        backgroundColor: ht.secondaryTab,
    },
}));

const HiddenInput = styled('input')({
    display: 'none',
});

/** Keep GL system Autocomplete list below the field (Popper otherwise flips up inside dialogs). */
const glAccountsUploadGlSystemAutocompleteComponentsProps = {
    popper: {
        placement: 'bottom-start',
        modifiers: [{ name: 'flip', enabled: false }],
        sx: { zIndex: 1800 },
    },
};

const fetchDefaultCashReconPropertyId = async (tenantId, token) => {
    if (!tenantId || !token) return null;
    try {
        const baseUrl = (process.env.REACT_APP_BACKEND_URL).replace(/\/+$/, '');
        const url = new URL(`${baseUrl}/conductor/cash-recon/properties/by-tenant-and-status`);
        url.searchParams.set('tenant_id', tenantId);
        url.searchParams.set('status', 'ACTIVE');
        const res = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
        });
        if (!res.ok) {
            console.warn('cash-recon properties by-tenant-and-status failed:', res.status);
            return null;
        }
        const list = await res.json().catch(() => []);
        if (!Array.isArray(list) || list.length === 0) return null;
        const byName = list.find((p) => String(p.propertyName || '').toLowerCase().trim() === 'default');
        const byCode = list.find((p) => String(p.propertyCode || '').toUpperCase().trim() === 'DF');
        const chosen = byName || byCode || list[0];
        return chosen?.propertyId ?? null;
    } catch (e) {
        console.error('fetchDefaultCashReconPropertyId:', e);
        return null;
    }
};

const extractSpreadsheetColumnHeaders = (file, arrayBuffer) => {
    if (file.name.endsWith('.csv')) {
        const text = new TextDecoder().decode(arrayBuffer);
        const rows = text.split('\n').map((row) => {
            const regex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
            return row.split(regex).map((cell) => cell.replace(/^"|"$/g, '').trim());
        });
        const headers = rows[0] || [];
        return headers.map((name, index) => ({
            index,
            name: name == null ? '' : String(name),
        }));
    }
    if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const row0 = json[0] || [];
        return row0.map((cell, index) => ({
            index,
            name: cell == null ? '' : String(cell).trim(),
        }));
    }
    throw new Error('Unsupported file type. Please upload a .csv, .xls, or .xlsx file.');
};

const normalizeSurrogateHeaderToken = (s) =>
    String(s || '')
        .trim()
        .replace(/\s+/g, '_')
        .toUpperCase();

/** Min 1, max 5: column 0 → `glCode1`; columns 1…n−1 → joined with "." (skipping blanks) → `glCode2` (may be empty). */
const GL_UPLOAD_COMBINATION_MIN = 1;
const GL_UPLOAD_COMBINATION_MAX = 5;

/** Map tenant-system `surrogateKey` (pipe-separated header tokens) to combination columns (1–GL_UPLOAD_COMBINATION_MAX). */
const resolveUploadCombinationFromSurrogateKey = (surrogateKey, headers) => {
    if (!surrogateKey || !Array.isArray(headers) || headers.length === 0) return null;
    const parts = splitSurrogateKeyTokens(surrogateKey);
    if (parts.length < 1 || parts.length > GL_UPLOAD_COMBINATION_MAX) return null;

    const findCol = (token) => {
        const t = normalizeSurrogateHeaderToken(token);
        return headers.find((h) => normalizeSurrogateHeaderToken(h.name) === t);
    };

    const cols = [];
    for (const part of parts) {
        const c = findCol(part);
        if (!c) return null;
        cols.push(c);
    }
    const indices = cols.map((c) => c.index);
    if (new Set(indices).size !== indices.length) return null;
    return cols;
};

const findUploadSystemRow = (systems, uploadGlSystemLabel) => {
    if (!uploadGlSystemLabel?.trim() || !Array.isArray(systems)) return undefined;
    const needle = String(uploadGlSystemLabel).trim();
    return (
        systems.find((s) => String(s.name ?? '').trim() === needle) ||
        systems.find((s) => String(s.name ?? '').trim().toLowerCase() === needle.toLowerCase())
    );
};

/** Ref-template mapping normalization (parity with `GLSyncGLMappingWorkflow.jsx`). */
const normalizeGlAccountRefMappingFromApi = (mappingObj) => {
    const out = {};
    if (!mappingObj || typeof mappingObj !== 'object') return out;
    Object.entries(mappingObj).forEach(([rawKey, v]) => {
        const key = String(rawKey).trim();
        if (!key || !v || v.isActive === false) return;
        out[key] = {
            ...v,
            targetColumn: String(v.targetColumn != null ? v.targetColumn : '').trim() || key,
            dataType: v.dataType || 'TEXT',
            isActive: v.isActive !== false,
        };
    });
    return out;
};

const GL_ACCOUNTS_TEMPLATE_CANONICAL = new Set([
    'GL_CODE',
    'GL_DESCRIPTION',
    'GL_ACCOUNT',
    'TRANSACTION_TYPE',
    'LOT_NO',
    'BATCH_ID',
    'CCY_CODE',
    'COMMENTS',
    'CURRENCY',
    'QUANTITY',
    'SCY_CODE',
    'FUND_CODE',
    'FUND_NAME',
    'ACCOUNT_NO',
    'AMOUNT_CCY',
    'AMOUNT_SCY',
    'DESCRIPTION',
    'ISSUER_CODE',
    'SECURITY_NO',
    'ACCOUNT_TYPE',
    'COMPANY_CODE',
    'COMPANY_TYPE',
    'DEBIT_AMOUNT',
    'POSTING_DATE',
    'TOTAL_AMOUNT',
    'CREDIT_AMOUNT',
    'DOCUMENT_DATE',
    'GL_JOIN_FIELD',
    'INVESTOR_CODE',
    'INVESTOR_NAME',
    'LOCAL_CURRENCY',
    'SOURCE_FILE_GL',
    'TRANSACTION_DATE',
    'INVESTOR_POOL_CODE',
    'LOCAL_DEBIT_AMOUNT',
    'LOCAL_TOTAL_AMOUNT',
    'LOCAL_CREDIT_AMOUNT',
    'EXTERNAL_DOCUMENT_NO',
]);

/** Workflow-style target hints → canonical GL Account upload columns (subset). */
const GL_ACCOUNT_REF_TARGET_TO_CANONICAL = {
    transactionDate: 'TRANSACTION_DATE',
    glAccountName: 'GL_DESCRIPTION',
    glAccountNo: 'GL_CODE',
    glCode: 'GL_CODE',
    glDescription: 'GL_DESCRIPTION',
    glAccount: 'GL_ACCOUNT',
    accountDisplay: 'GL_ACCOUNT',
    transactionType: 'TRANSACTION_TYPE',
};

const convertGlAccountTemplateCellValue = (value, dataType) => {
    if (value === '' || value == null) return '';
    const type = (dataType || 'TEXT').toUpperCase();
    if (['NUMBER', 'INTEGER', 'FLOAT', 'DECIMAL'].includes(type)) {
        const strValue = String(value).trim();
        const numValue = strValue.replace(/[^0-9.-]/g, '');
        if (numValue === '' || numValue === '-') return '';
        return Number(numValue);
    }
    return String(value).replace(/\s+/g, ' ').trim();
};

/** After template mapping, drop raw file headers so uploaded JSON is canonical-only (parity with GLSyncGLMappingWorkflow). */
const stripMappedSourceColumnsFromRow = (out, sourceRow, excelField, preserveKeysLower = new Set()) => {
    const base = String(excelField || '').trim().toLowerCase();
    if (!base) return;
    for (const k of [...Object.keys(out)]) {
        if (preserveKeysLower.has(k.toLowerCase())) continue;
        if (!Object.prototype.hasOwnProperty.call(sourceRow, k)) continue;
        const kl = k.toLowerCase();
        if (kl === base || kl.startsWith(`${base}_`)) {
            delete out[k];
        }
    }
};

/**
 * Map spreadsheet rows onto canonical keys used by chart-of-account upload (subset of workflow `applyRefTemplateToRows`).
 * Does not alter surrogate-key / combination paths — callers use this only when combination mapping is inactive.
 */
const applyGlAccountRefTemplateToRows = (excelData, template, excelColumnByTemplateField) => {
    const mappings = template?.mappings || {};
    const fieldSpecs = [];
    Object.entries(mappings).forEach(([templateKeyRaw, m]) => {
        if (!m || m.isActive === false) return;
        const templateKey = String(templateKeyRaw).trim();
        const excelField =
            excelColumnByTemplateField?.[templateKey] ?? excelColumnByTemplateField?.[templateKeyRaw];
        if (!excelField || String(excelField).trim() === '') return;
        const targetCol = String(m.targetColumn || '').trim();
        const tkUpper = templateKey.toUpperCase();

        let canonical =
            GL_ACCOUNT_REF_TARGET_TO_CANONICAL[targetCol] ||
            GL_ACCOUNT_REF_TARGET_TO_CANONICAL[templateKey] ||
            null;
        if (!canonical && /^[A-Z0-9_]+$/.test(tkUpper)) {
            canonical = tkUpper;
        }
        if (!canonical && targetCol && targetCol === targetCol.toUpperCase() && /^[A-Z0-9_]+$/.test(targetCol)) {
            canonical = targetCol;
        }
        if (!canonical || !GL_ACCOUNTS_TEMPLATE_CANONICAL.has(canonical)) return;

        fieldSpecs.push({
            excelField: String(excelField).trim(),
            canonical,
            dataType: m.dataType || 'TEXT',
        });
    });

    if (fieldSpecs.length === 0) return excelData;

    return excelData.map((row) => {
        const out = { ...row };
        fieldSpecs.forEach(({ excelField, canonical, dataType }) => {
            if (!(excelField in row)) return;
            const v = convertGlAccountTemplateCellValue(row[excelField], dataType);
            if (v !== '' && v != null) out[canonical] = v;
            stripMappedSourceColumnsFromRow(out, row, excelField, new Set([String(canonical).toLowerCase()]));
        });
        return out;
    });
};

const initializeGlAccountExcelColumnMap = (template, excelFieldNames) => {
    const next = {};
    Object.entries(template?.mappings || {}).forEach(([templateKeyRaw, m]) => {
        const templateKey = String(templateKeyRaw).trim();
        if (!m || m.isActive === false) return;
        const expectedHeader = String(m.targetColumn || templateKey).trim();
        const auto = excelFieldNames.find(
            (f) => String(f).trim().toLowerCase() === expectedHeader.toLowerCase()
        );
        next[templateKey] = auto ? String(auto) : '';
    });
    return next;
};

/** Same canonical resolution as `applyGlAccountRefTemplateToRows` (for mandatory-field checks). */
const resolveGlAccountTemplateFieldCanonical = (templateKeyRaw, spec) => {
    if (!spec || spec.isActive === false) return null;
    const templateKey = String(templateKeyRaw).trim();
    const targetCol = String(spec.targetColumn || '').trim();
    const tkUpper = templateKey.toUpperCase();
    let canonical =
        GL_ACCOUNT_REF_TARGET_TO_CANONICAL[targetCol] ||
        GL_ACCOUNT_REF_TARGET_TO_CANONICAL[templateKey] ||
        null;
    if (!canonical && /^[A-Z0-9_]+$/.test(tkUpper)) canonical = tkUpper;
    if (!canonical && targetCol && targetCol === targetCol.toUpperCase() && /^[A-Z0-9_]+$/.test(targetCol)) {
        canonical = targetCol;
    }
    if (!canonical || !GL_ACCOUNTS_TEMPLATE_CANONICAL.has(canonical)) return null;
    return canonical;
};

const GL_ACCOUNT_UPLOAD_REQUIRED_HEADERS = ['GL_CODE', 'GL_ACCOUNT', 'GL_DESCRIPTION'];

/** First sheet → header-keyed rows (same behavior as workflow `extractFieldsFromSheet`). */
const extractGlAccountFieldsFromSheet = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

    if (!jsonData || jsonData.length === 0) {
        return { fields: [], data: [] };
    }

    const headers = jsonData[0].map((h) => (h ? h.toString().trim() : '')).filter((h) => h !== '');
    const rows = jsonData.slice(1).filter((row) => row.some((cell) => cell !== '' && cell !== null));

    const dataRows = rows.map((row) => {
        const obj = {};
        headers.forEach((header, index) => {
            obj[header] = row[index] ?? '';
        });
        return obj;
    });

    return { fields: headers, data: dataRows };
};

const glAccountMappedObjectsToParsedRows = (mappedRows, glSystem) => {
    return mappedRows.map((row) => {
        const code = String(row.GL_CODE ?? '').trim();
        const tt = String(row.TRANSACTION_TYPE ?? '').trim();
        const glDescription = String(row.GL_DESCRIPTION ?? '').trim();
        const glAccount = String(row.GL_ACCOUNT ?? '').trim();
        const optional = {
            accountType: String(row.ACCOUNT_TYPE ?? '').trim() || null,
            accountSubType: String(row.ACCOUNT_SUB_TYPE ?? '').trim() || null,
            normalBalance: String(row.NORMAL_BALANCE ?? '').trim() || null,
            groupCode: String(row.GROUP_CODE ?? '').trim() || null,
            currency: String(row.CURRENCY ?? '').trim() || null,
            transactionType: tt || null,
            glJoinField: String(row.GL_JOIN_FIELD ?? '').trim() || null,
        };
        return {
            glCode1: code,
            glCode2: tt,
            glDescription,
            glAccount,
            ...optional,
            glSystem: String(glSystem || '').toUpperCase(),
            glSystemVersion: '1.0',
        };
    });
};

const GLAccounts = () => {
    const { hasPermission, isHarmonateUser } = useUserRights();

    // ✅ States
    const [selectedSystem, setSelectedSystem] = useState('');
    /** GL header from selected upload row — required for chart-of-account by-tenant-with-account-type-property */
    const [selectedGlHeaderId, setSelectedGlHeaderId] = useState(null);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [error, setError] = useState(null);

    const { toastState, showToast: enqueueToast, hideToast } = useToast();

    // ✅ Recent Uploads States
    const [recentUploads, setRecentUploads] = useState([]);
    const [loadingUploads, setLoadingUploads] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedUploadRow, setSelectedUploadRow] = useState(null);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [uploadSearchQuery, setUploadSearchQuery] = useState('');
    // Pagination state for accounts DataGrid
    const [accountsPaginationModel, setAccountsPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerData, setViewerData] = useState(null);
    const [viewerLoading, setViewerLoading] = useState(false);
    const [viewDataOpen, setViewDataOpen] = useState(false);
    const [viewDataData, setViewDataData] = useState(null);
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteAction, setDeleteAction] = useState(null); // 'upload' or null
    const [itemToDelete, setItemToDelete] = useState(null);
    /** Upload row used to load accounts (for highlight) */
    const [selectedUploadRowId, setSelectedUploadRowId] = useState(null);
    /** Document details + audit logs (GLSync uploads parity) */
    const [auditLogs, setAuditLogs] = useState([]);
    const [documentDetails, setDocumentDetails] = useState(null);
    const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);
    const [auditLogsPaginationModel, setAuditLogsPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [logsModalSourceRow, setLogsModalSourceRow] = useState(null);
    const [logsJsonDialogOpen, setLogsJsonDialogOpen] = useState(false);
    const [logsJsonLoading, setLogsJsonLoading] = useState(false);
    const [logsJsonError, setLogsJsonError] = useState(null);
    const [logsJsonData, setLogsJsonData] = useState(null);
    const [logsExtractionDialogOpen, setLogsExtractionDialogOpen] = useState(false);
    const [logsExtractionLoading, setLogsExtractionLoading] = useState(false);
    const [logsExtractionDetails, setLogsExtractionDetails] = useState(null);
    /** Accounts shown in a dialog (row click or manual GL system) */
    const [accountsDialogOpen, setAccountsDialogOpen] = useState(false);
    // ✅ Delete Account States
    const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);

    const API_BASE = process.env.REACT_APP_API_URL || '';
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadGlSystem, setUploadGlSystem] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [, setUnitOfWorkId] = useState('');
    const [uploadCurrentStep, setUploadCurrentStep] = useState('');
    const [uploadError, setUploadError] = useState(null);
    const [uploadHeaderNotes, setUploadHeaderNotes] = useState('');
    const [uploadDocumentTypes, setUploadDocumentTypes] = useState([]);
    const [loadingUploadDocumentTypes, setLoadingUploadDocumentTypes] = useState(false);
    const [selectedUploadDocumentType, setSelectedUploadDocumentType] = useState('');
    const [uploadSystems, setUploadSystems] = useState([]);
    const [loadingUploadSystems, setLoadingUploadSystems] = useState(false);
    /** Optional fund scope for COA upload (public.funds). Null = unscoped. */
    const [uploadFunds, setUploadFunds] = useState([]);
    const [loadingUploadFunds, setLoadingUploadFunds] = useState(false);
    const [uploadSelectedFund, setUploadSelectedFund] = useState(null);
    const [uploadDragActive, setUploadDragActive] = useState(false);
    const [uploadFileHeaders, setUploadFileHeaders] = useState([]);
    const [uploadColumnCombination, setUploadColumnCombination] = useState([]);
    const [uploadSelectedSourceColumn, setUploadSelectedSourceColumn] = useState(null);
    const [uploadHeadersLoading, setUploadHeadersLoading] = useState(false);
    /** Optional ref-templates (disabled when surrogate combination columns are in use — same semantics as workflow). */
    const [gaTemplates, setGaTemplates] = useState([]);
    const [gaLoadingTemplates, setGaLoadingTemplates] = useState(false);
    const [gaSelectedTemplate, setGaSelectedTemplate] = useState('');
    const [gaExcelColumnByTemplateField, setGaExcelColumnByTemplateField] = useState({});
    const [showMissingTemplateFields, setShowMissingTemplateFields] = useState(false);
    const [gaTemplateMappingsEditorOpen, setGaTemplateMappingsEditorOpen] = useState(false);
    /** Raw API `mapping` overlays from the template drawer for this upload session only (no PUT). */
    const [localGaRefTemplateMappingById, setLocalGaRefTemplateMappingById] = useState({});
    const [refreshingCoa, setRefreshingCoa] = useState(false);
    const [pushingCoa, setPushingCoa] = useState(false);
    /** null | 'refresh' | 'push' — confirmation before Snowflake ↔ Postgres COA sync */
    const [coaConfirmAction, setCoaConfirmAction] = useState(null);

    const [dateTimeFormat, setDateTimeFormat] = useState('DD/MM/YYYY hh:mm:ss a');
    // Initialize formats when component mounts
    useEffect(() => {
        const tenantId = localStorage.getItem('tenant_id');
        if (tenantId) {
            initializeFormats(tenantId).then(() => {
                const config = getFormatConfig();
                if (config.DateTimeFormat) {
                    setDateTimeFormat(config.DateTimeFormat);
                }
            });
        }
    }, []);
    // Refetch recent uploads when dateTimeFormat changes
    useEffect(() => {
        if (dateTimeFormat) {
            fetchRecentUploads();
        }
    }, [dateTimeFormat]);

    const resolveSnowflakeTenantId = () => {
        // DEV UI often has tenant_integration = DEV_T_101; COA rows use T_247.
        const raw = String(localStorage.getItem('tenant_integration') || '').trim();
        if (!raw) return '';
        const upper = raw.toUpperCase();
        const dbToLogical = {
            DEV_T_101: 'T_247',
        };
        if (dbToLogical[upper]) return dbToLogical[upper];
        if (upper.startsWith('T_')) return upper;
        if (/^\d+$/.test(upper)) return `T_${upper}`;
        return upper;
    };

    /** Sync DEV_ADMIN.POSTGRES.CHART_OF_ACCOUNT → Postgres core.chart_of_account (GL Sync only). */
    const handleRefreshCoa = async () => {
        if (refreshingCoa || pushingCoa) return;
        const token = localStorage.getItem('token');
        const tenantUuid = localStorage.getItem('tenant_id');
        const snowflakeTenantId = resolveSnowflakeTenantId();
        if (!token) {
            showToast('Authentication required', 'error');
            return;
        }
        if (!tenantUuid || !snowflakeTenantId) {
            showToast('Tenant id / integration id missing — cannot refresh COA', 'error');
            return;
        }
        try {
            setRefreshingCoa(true);
            const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '/');
            const url = new URL(`${baseUrl}fastapi/v1/gl-sync/chart-of-accounts/sync-from-snowflake`);
            url.searchParams.set('snowflake_tenant_id', snowflakeTenantId);
            url.searchParams.set('tenant_uuid', tenantUuid);
            const resp = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
            });
            const body = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                const detail = body?.detail || body?.message || resp.statusText || 'Refresh COA failed';
                throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
            }
            const inserted = Number(body?.inserted) || 0;
            const updated = Number(body?.updated) || 0;
            const skipped = Number(body?.skipped) || 0;
            const fetched = Number(body?.fetched_rows) || 0;
            setCoaConfirmAction(null);
            showToast(
                body?.message
                    || `COA refreshed from Snowflake (${snowflakeTenantId}). Fetched ${fetched}, inserted ${inserted}, updated ${updated}, skipped ${skipped}.`,
                'success'
            );
            // Reload Uploads grid so new/updated gl_header rows appear without a hard refresh
            await fetchRecentUploads();
        } catch (error) {
            showToast(error?.message || 'Failed to refresh COA from Snowflake', 'error');
        } finally {
            setRefreshingCoa(false);
        }
    };

    /** Push Postgres core.chart_of_account (GL_SYNC) → Snowflake POSTGRES.CHART_OF_ACCOUNT. */
    const handlePushCoa = async () => {
        if (refreshingCoa || pushingCoa) return;
        const token = localStorage.getItem('token');
        const tenantUuid = localStorage.getItem('tenant_id');
        const snowflakeTenantId = resolveSnowflakeTenantId();
        if (!token) {
            showToast('Authentication required', 'error');
            return;
        }
        if (!tenantUuid || !snowflakeTenantId) {
            showToast('Tenant id / integration id missing — cannot push COA', 'error');
            return;
        }
        try {
            setPushingCoa(true);
            const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '/');
            const url = new URL(`${baseUrl}fastapi/v1/gl-sync/chart-of-accounts/sync-to-snowflake`);
            url.searchParams.set('snowflake_tenant_id', snowflakeTenantId);
            url.searchParams.set('tenant_uuid', tenantUuid);
            const resp = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
            });
            const body = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                const detail = body?.detail || body?.message || resp.statusText || 'Push COA failed';
                throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
            }
            const upserted = Number(body?.upserted) || 0;
            const skipped = Number(body?.skipped) || 0;
            const fetched = Number(body?.fetched_rows) || 0;
            setCoaConfirmAction(null);
            showToast(
                body?.message
                    || `COA pushed to Snowflake (${body?.snowflake_tenant_id || snowflakeTenantId}). Fetched ${fetched}, upserted ${upserted}, skipped ${skipped}.`,
                'success'
            );
        } catch (error) {
            showToast(error?.message || 'Failed to push COA to Snowflake', 'error');
        } finally {
            setPushingCoa(false);
        }
    };

    const handleDeleteAccount = (account) => {
        setAccountToDelete(account);
        setDeleteAccountDialogOpen(true);
    };

    const confirmDeleteAccount = async () => {
        if (accountToDelete) {
            try {
                const token = localStorage.getItem('token');
                const tenantId = localStorage.getItem('tenant_id');

                if (!token || !tenantId) {
                    throw new Error('Authentication required');
                }
                console.log('accountToDelete object:', accountToDelete);
                // Log the account deletion attempt
                try {
                    await AuditLoggers.glAccountDeletionInitiated(
                        accountToDelete.glId,
                        accountToDelete.glAccount || accountToDelete.glCode || 'Unknown GL Account',
                        {
                            accountNumber: accountToDelete.glCode,
                            description: accountToDelete.glDescription,
                            glSystem: accountToDelete.glSystem,
                            tenantId: tenantId
                        }
                    );
                } catch (logError) {
                    console.error('Error logging GL account deletion:', logError);
                    // Continue with deletion even if logging fails
                }

                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}conductor/gl-sync/accounts/${accountToDelete.glId}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error(`Failed to delete account: ${response.status}`);
                }

                // Update local state after successful deletion
                setAccounts(prevAccounts => prevAccounts.filter(acc => acc.glId !== accountToDelete.glId));
                showToast('Account deleted successfully', 'success');
            } catch (error) {
                console.error('Error deleting account:', error);
                showToast(`Failed to delete account: ${error.message}`, 'error');
            }
        }
        setDeleteAccountDialogOpen(false);
        setAccountToDelete(null);
    };

    const cancelDeleteAccount = () => {
        setDeleteAccountDialogOpen(false);
        setAccountToDelete(null);
    };

    const handleEditAccount = (account) => {
        // You can implement the edit functionality here
        // For example, open a dialog or navigate to an edit page
        console.log('Edit account:', account);
        showToast('Edit functionality will be implemented here', 'info');
    };

    const handleDeleteAllAccounts = () => {
        setDeleteAllDialogOpen(true);
    };

    const confirmDeleteAll = async () => {
        if (!selectedSystem) return;

        try {
            setLoadingAccounts(true);
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_id');
            const userId = localStorage.getItem('user_id');
            const username = localStorage.getItem('username');

            if (!token || !tenantId) {
                throw new Error('Authentication required');
            }

            // Get the count of accounts to be deleted for logging
            const accountCount = accounts.length;

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}conductor/gl-sync/gl-accounts/system/${selectedSystem}?tenantId=${tenantId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to delete all accounts: ${response.status}`);
            }

            // Log the deletion
            try {
                await AuditLoggers.glAccountsDeleted(
                    selectedSystem,
                    accountCount,
                    null, // No specific account ID for bulk delete
                    {
                        timestamp: new Date().toISOString(),
                        userId: userId,
                        username: username,
                        tenantId: tenantId,
                        action: 'Bulk Delete Accounts'
                    }
                );
            } catch (logError) {
                console.error('Error logging account deletion:', logError);
                // Don't block the operation if logging fails
            }

            // Update local state after successful deletion
            setAccounts([]);
            showToast(`All accounts for ${selectedSystem} have been removed`, 'success');
        } catch (error) {
            console.error('Error deleting all accounts:', error);
            showToast(`Failed to delete all accounts: ${error.message}`, 'error');
        } finally {
            setLoadingAccounts(false);
            setDeleteAllDialogOpen(false);
        }
    };

    const cancelDeleteAll = () => {
        setDeleteAllDialogOpen(false);
    };

    // ✅ Function to fetch recent uploads
    const fetchRecentUploads = async () => {
        setLoadingUploads(true);
        try {
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_id');

            if (!token || !tenantId) {
                console.error('Missing token or tenant ID');
                return;
            }

            // First, get GL document type ID
            const docTypeResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/document-types/by-category/GL`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!docTypeResponse.ok) {
                throw new Error('Failed to fetch GL document types');
            }

            const docTypeData = await docTypeResponse.json();

            if (docTypeData.length === 0) {
                console.log('No GL document types found');
                setRecentUploads([]);
                return;
            }

            // Find the "GL Account" document type ID
            const glAccountDocType = docTypeData.find(doc => doc.name === 'GL Account');

            if (!glAccountDocType) {
                console.log('GL Account document type not found');
                setRecentUploads([]);
                return;
            }

            const glDocTypeId = glAccountDocType.id;

            const fileInstancesUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/by-document-type/${glDocTypeId}?tenantId=${tenantId}`;
            const glHeadersUrl = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-headers/by-tenant`);
            glHeadersUrl.searchParams.set('tenant_id', tenantId);
            glHeadersUrl.searchParams.set('document_type', 'GL Account');
            const fundsBase = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');
            const fundsUrl = `${fundsBase}/conductor/funds/by-tenant-entity?tenantId=${encodeURIComponent(tenantId)}`;

            const [fileInstancesResponse, glHeadersResponse, fundsResponse] = await Promise.all([
                fetch(fileInstancesUrl, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }),
                fetch(glHeadersUrl.toString(), {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }),
                fetch(fundsUrl, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }),
            ]);

            if (!fileInstancesResponse.ok) {
                throw new Error('Failed to fetch file instances');
            }

            const fileInstancesData = await fileInstancesResponse.json();
            const fileList = Array.isArray(fileInstancesData) ? fileInstancesData : [];

            let headersList = [];
            if (glHeadersResponse.ok) {
                const rawHeaders = await glHeadersResponse.json();
                headersList = Array.isArray(rawHeaders) ? rawHeaders : [];
            } else {
                console.warn('gl-headers request failed:', glHeadersResponse.status);
            }

            const fundsById = new Map();
            if (fundsResponse.ok) {
                const fundsData = await fundsResponse.json();
                (Array.isArray(fundsData) ? fundsData : []).forEach((f) => {
                    const id = f.id || f.masterFundId;
                    if (id) {
                        fundsById.set(String(id), {
                            fundName: f.fundName || '',
                            fundCode: f.fundCode || '',
                        });
                    }
                });
            }

            const findFileInstanceForHeader = (header) => {
                const docId = header.documentId;
                const fileName = (header.fileName || '').trim();
                if (docId) {
                    const byDoc = fileList.find(
                        (f) => f.id === docId || f.integrationId === docId
                    );
                    if (byDoc) return byDoc;
                }
                if (fileName) {
                    return fileList.find((f) => (f.name || '').trim() === fileName);
                }
                return undefined;
            };

            // Prefer gl-headers for grid display; keep file-instance ids for workflow / view / delete
            let transformedData;
            if (headersList.length > 0) {
                const sorted = [...headersList].sort((a, b) => {
                    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return tb - ta;
                });
                transformedData = sorted.map((header, index) => {
                    const fi = findFileInstanceForHeader(header);
                    // Prefer gl_header.gl_system as stored in COA (e.g. TALLY_GL).
                    // Do not strip "_GL" — the accounts API matches on the full code.
                    const sourceFromHeader = (header.glSystem || '').trim();
                    const sourceFromFi = (fi?.sourceGeneralLedger || '').trim();
                    const fundId = header.fundId ?? header.fund_id ?? null;
                    const fundMeta = fundId ? fundsById.get(String(fundId)) : null;
                    const fundCode = fundMeta?.fundCode || '';
                    const fundName = fundMeta?.fundName || '';
                    const fundLabel =
                        fundCode && fundName
                            ? `${fundCode} (${fundName})`
                            : fundCode || fundName || '—';
                    return {
                        id: header.glHeaderId || `gl-header-${index}`,
                        name: header.fileName || 'Unknown File',
                        source: sourceFromHeader || sourceFromFi || 'Unknown',
                        fundId,
                        fundCode,
                        fundName,
                        fundLabel,
                        surrogateKey: header.surrogateKey ?? header.surrogate_key ?? '—',
                        destination: '-',
                        date: header.createdAt
                            ? formatDateTime(header.createdAt, dateTimeFormat)
                            : 'Unknown Date',
                        integrationId: fi?.integrationId ?? header.documentId,
                        unitOfWorkId: fi?.unitOfWorkId,
                        fileInstanceId: fi?.id ?? null,
                        fastApiFileInstanceId: fi?.integrationId ?? fi?.fastApiFileInstanceId ?? null,
                        processId: fi?.processId ?? null,
                        glHeaderId: header.glHeaderId,
                        documentId: header.documentId,
                        documentType: header.documentType,
                        glVersion: header.glVersion ?? '-',
                    };
                });
            } else {
                // Keep file-instances fetch (used for merging when headers exist); do not backfill grid from it
                transformedData = [];
            }

            setRecentUploads(transformedData);
        } catch (error) {
            console.error('Error fetching recent uploads:', error);
            showToast(`Failed to load recent uploads: ${error.message}`, 'error');
        } finally {
            setLoadingUploads(false);
        }
    };

    // ✅ Fetch accounts when selected upload row supplies system + gl header id
    useEffect(() => {
        if (selectedSystem && selectedGlHeaderId) {
            fetchGLAccounts(selectedSystem, selectedGlHeaderId);
        } else {
            setAccounts([]);
        }
    }, [selectedSystem, selectedGlHeaderId]);

    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, [uploadSearchQuery]);

    const filteredUploadRows = useMemo(() => {
        const q = uploadSearchQuery.trim().toLowerCase();
        if (!q) return recentUploads;
        return recentUploads.filter((row) => {
            const hay = [
                row.name,
                row.source,
                row.fundLabel,
                row.fundName,
                row.fundCode,
                row.surrogateKey,
                row.glVersion,
                row.date,
            ]
                .map((v) => (v == null ? '' : String(v)))
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [recentUploads, uploadSearchQuery]);

    const normalizeGlSystemQueryParam = (systemName) => toChartOfAccountGlSystem(systemName);

    // ✅ Function to fetch GL accounts (cash-recon chart of account)
    const fetchGLAccounts = async (systemName, glHeaderId) => {
        setLoadingAccounts(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_id');

            if (!token || !tenantId) {
                throw new Error('Authentication required');
            }

            if (!glHeaderId) {
                setAccounts([]);
                setError('GL header is required to load chart of accounts.');
                return;
            }

            const glSystem = normalizeGlSystemQueryParam(systemName);
            const url = new URL(
                `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property`
            );
            url.searchParams.set('tenant_id', tenantId);
            url.searchParams.set('gl_system', glSystem);
            url.searchParams.set('system_type', 'GL_SYNC');
            url.searchParams.set('gl_header_id', glHeaderId);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch GL accounts: ${response.status}`);
            }

            const data = await response.json();
            const rows = Array.isArray(data) ? data : [];
            // Map ledgerAccountId → glId for DataGrid row id and existing delete API
            setAccounts(
                rows.map((item) => ({
                    ...item,
                    glId: item.ledgerAccountId ?? item.glId,
                }))
            );
        } catch (error) {
            console.error('Error fetching GL accounts:', error);
            setError(`Failed to load GL accounts: ${error.message}`);
            showToast(`Failed to load GL accounts: ${error.message}`, 'error');
            setAccounts([]);
        } finally {
            setLoadingAccounts(false);
        }
    };

    /** Same shared Toast as GL Mapping; anchor bottom-left (Mapping-style workload toast zone). */
    const GL_ACCOUNTS_TOAST_POSITION = { vertical: 'bottom', horizontal: 'left' };

    const showToast = (message, severity = 'error') => {
        enqueueToast({
            message,
            severity,
            position: GL_ACCOUNTS_TOAST_POSITION,
            autoHideDuration: severity === 'success' ? 10000 : 6000,
        });
    };

    const handleLogsModalViewRawJson = async (file) => {
        try {
            setLogsJsonLoading(true);
            setLogsJsonError(null);
            setLogsJsonData(null);
            setLogsJsonDialogOpen(true);

            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_id');

            if (!file?.unitOfWorkId) {
                throw new Error('File does not have a unitOfWorkId');
            }
            if (!file?.fastApiFileInstanceId || !file?.processId) {
                throw new Error('File does not have integration / workflow identifiers for extracted JSON');
            }

            const s3Path = `t-${tenantId}/uow-${file.unitOfWorkId}/extracted/output/FI-${file.fastApiFileInstanceId}-WI-${file.processId}.json`;

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/presigned-url-for-path` +
                    `?s3Path=${encodeURIComponent(s3Path)}` +
                    `&tenantId=${encodeURIComponent(tenantId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.message ||
                        `Failed to get presigned URL: ${response.status} ${response.statusText}`
                );
            }

            const { url } = await response.json();

            const jsonResponse = await fetch(url, {
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                },
            });

            if (!jsonResponse.ok) {
                throw new Error('Failed to fetch extracted JSON data');
            }

            const data = await jsonResponse.json();
            setLogsJsonData({
                content: JSON.stringify(data, null, 2),
                downloadUrl: url,
                isBinary: false,
                fileName: `FI-${file.fileInstanceId ?? file.id}.json`,
            });
        } catch (error) {
            console.error('Error:', error);
            setLogsJsonError(error.message);
            showToast(`Failed to load file: ${error.message}`, 'error');
        } finally {
            setLogsJsonLoading(false);
        }
    };

    const handleLogsModalSnowflakeClick = async () => {
        try {
            setLogsExtractionLoading(true);
            const token = localStorage.getItem('token');
            const tenantIntegration = localStorage.getItem('tenant_integration');

            setLogsExtractionDetails(null);

            const file = logsModalSourceRow;
            if (!file) {
                showToast('No file context for extraction details', 'error');
                return;
            }

            const fileInstanceId = file.fastApiFileInstanceId;
            const workflowInstanceId = file.processId;

            if (!fileInstanceId || !workflowInstanceId) {
                showToast('Could not find file instance ID / workflow instance ID on this upload row', 'error');
                return;
            }

            let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/conductor-extraction-json?file_instance_id=${fileInstanceId}&workflow_instance_id=${workflowInstanceId}`;
            if (tenantIntegration) {
                apiUrl += `&tenant_id=${encodeURIComponent(tenantIntegration)}`;
            }

            const response = await fetch(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setLogsExtractionDetails(data);
            setLogsExtractionDialogOpen(true);
        } catch (error) {
            console.error('Error fetching extraction details:', error);
            showToast('Failed to load extraction details', 'error');
        } finally {
            setLogsExtractionLoading(false);
        }
    };

    const resetGaUploadTemplateUi = () => {
        setGaSelectedTemplate('');
        setGaExcelColumnByTemplateField({});
        setGaTemplateMappingsEditorOpen(false);
    };

    const uploadSourceColumnsAvailable = useMemo(() => {
        const used = new Set(uploadColumnCombination.map((c) => c.index));
        return uploadFileHeaders.filter((h) => !used.has(h.index));
    }, [uploadFileHeaders, uploadColumnCombination]);

    /** Surrogate combination path — ref-template upload is suppressed so existing logic is unchanged. */
    const gaCombinationPathActive = useMemo(
        () => uploadColumnCombination.length >= GL_UPLOAD_COMBINATION_MIN,
        [uploadColumnCombination.length]
    );

    const fetchGlAccountRefTemplates = useCallback(async () => {
        setGaLoadingTemplates(true);
        try {
            const token = getUploadAuthToken();
            if (!token || !selectedUploadDocumentType || !uploadGlSystem?.trim()) {
                setGaTemplates([]);
                setGaSelectedTemplate('');
                return;
            }
            const systemRow = findUploadSystemRow(uploadSystems, uploadGlSystem);
            const glSystemId = systemRow?.systemId || systemRow?.system_id || systemRow?.id;
            if (glSystemId == null || glSystemId === '') {
                setGaTemplates([]);
                setGaSelectedTemplate('');
                return;
            }
            const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
            const url = `${baseUrl}/conductor/ref-templates/by-document-and-gl?documentTypeId=${encodeURIComponent(
                selectedUploadDocumentType
            )}&glSystemId=${encodeURIComponent(String(glSystemId))}`;
            const resp = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!resp.ok) {
                setGaTemplates([]);
                setGaSelectedTemplate('');
                return;
            }
            const data = await resp.json();
            if (!Array.isArray(data) || data.length === 0) {
                setGaTemplates([]);
                setGaSelectedTemplate('');
                return;
            }
            const transformed = data.map((item) => {
                let rawMapping = item.mapping;
                if (typeof rawMapping === 'string') {
                    try {
                        rawMapping = JSON.parse(rawMapping);
                    } catch {
                        rawMapping = {};
                    }
                }
                if (!rawMapping || typeof rawMapping !== 'object') rawMapping = {};
                const tid = item.templateId ?? item.id;
                return {
                    id: tid,
                    name: item.templateName ?? item.name ?? 'Template',
                    description: item.description || '',
                    mappings: normalizeGlAccountRefMappingFromApi(rawMapping),
                    apiTemplate: {
                        ...item,
                        templateId: tid,
                        templateName: item.templateName ?? item.name ?? 'Template',
                        mapping: rawMapping,
                    },
                };
            });
            setGaTemplates(transformed);
            setGaSelectedTemplate((prev) =>
                prev && transformed.some((x) => x.id === prev) ? prev : transformed[0]?.id || ''
            );
        } catch (e) {
            console.error('Error fetching GL Account ref-templates:', e);
            setGaTemplates([]);
            setGaSelectedTemplate('');
        } finally {
            setGaLoadingTemplates(false);
        }
    }, [selectedUploadDocumentType, uploadGlSystem, uploadSystems]);

    useEffect(() => {
        if (!uploadDialogOpen) return;
        fetchGlAccountRefTemplates();
    }, [uploadDialogOpen, fetchGlAccountRefTemplates]);

    useEffect(() => {
        setLocalGaRefTemplateMappingById({});
    }, [uploadGlSystem, selectedUploadDocumentType]);

    useEffect(() => {
        if (!uploadDialogOpen) {
            setLocalGaRefTemplateMappingById({});
        }
    }, [uploadDialogOpen]);

    const activeGlAccountTemplate = useMemo(() => {
        if (!gaSelectedTemplate) return null;
        const base = gaTemplates.find((t) => t.id === gaSelectedTemplate);
        if (!base) return null;
        const localRaw = localGaRefTemplateMappingById[gaSelectedTemplate];
        if (!localRaw) return base;
        return {
            ...base,
            mappings: normalizeGlAccountRefMappingFromApi(localRaw),
            apiTemplate: { ...base.apiTemplate, mapping: localRaw },
        };
    }, [gaTemplates, gaSelectedTemplate, localGaRefTemplateMappingById]);

    const activeGaApiTemplateForEditor = useMemo(() => {
        if (!gaSelectedTemplate) return null;
        const base = gaTemplates.find((t) => t.id === gaSelectedTemplate)?.apiTemplate ?? null;
        if (!base) return null;
        const localRaw = localGaRefTemplateMappingById[gaSelectedTemplate];
        if (!localRaw) return base;
        return { ...base, mapping: localRaw };
    }, [gaTemplates, gaSelectedTemplate, localGaRefTemplateMappingById]);

    useEffect(() => {
        if (!gaSelectedTemplate) {
            setGaExcelColumnByTemplateField((prev) => (Object.keys(prev).length === 0 ? prev : {}));
            return;
        }
        const t = activeGlAccountTemplate;
        if (!t) return;
        const names = uploadFileHeaders.map((h) => String(h.name ?? '').trim()).filter(Boolean);
        if (names.length === 0) {
            setGaExcelColumnByTemplateField({});
            return;
        }
        setGaExcelColumnByTemplateField(initializeGlAccountExcelColumnMap(t, names));
    }, [gaSelectedTemplate, activeGlAccountTemplate, uploadFileHeaders]);

    const glAccountTemplateColumnRows = useMemo(() => {
        if (!activeGlAccountTemplate) return [];
        const headerNames = uploadFileHeaders
            .map((h) => String(h.name ?? '').trim())
            .filter(Boolean);
        return Object.entries(activeGlAccountTemplate.mappings || {})
            .map(([templateKeyRaw, spec]) => {
                const templateKey = String(templateKeyRaw).trim();
                if (!spec || spec.isActive === false) return null;
                const expectedHeader = String(spec.targetColumn || templateKey).trim();
                const selectedCol = String(gaExcelColumnByTemplateField[templateKey] || '').trim();
                const autoMatched = headerNames.find(
                    (hn) => hn.toLowerCase() === expectedHeader.toLowerCase()
                );
                const displayFileCol = selectedCol || autoMatched || 'Missing';
                const matched = Boolean(displayFileCol && displayFileCol !== 'Missing');
                const canonicalKey = resolveGlAccountTemplateFieldCanonical(templateKeyRaw, spec);
                return {
                    templateKey,
                    expectedHeader,
                    matched,
                    displayFileCol,
                    canonicalKey,
                };
            })
            .filter(Boolean)
            .sort((a, b) => {
                if (a.matched === b.matched) return 0;
                return a.matched ? -1 : 1;
            });
    }, [activeGlAccountTemplate, uploadFileHeaders, gaExcelColumnByTemplateField]);

    const missingTemplateColumnCount = useMemo(
        () => glAccountTemplateColumnRows.filter((row) => !row.matched).length,
        [glAccountTemplateColumnRows]
    );

    /** Blocks Upload until GL_CODE / GL_ACCOUNT / GL_DESCRIPTION are satisfied (raw headers or template mapping). */
    const glAccountUploadMandatoryMessage = useMemo(() => {
        if (!uploadFile || uploadHeadersLoading || uploadFileHeaders.length === 0) return null;
        const raw = new Set(
            uploadFileHeaders.map((h) => String(h.name ?? '').trim().toUpperCase()).filter(Boolean)
        );
        if (gaCombinationPathActive) {
            if (!raw.has('GL_ACCOUNT') || !raw.has('GL_DESCRIPTION')) {
                return 'Row 1 must include GL_ACCOUNT and GL_DESCRIPTION. GL_CODE is built from your combination columns.';
            }
            return null;
        }
        if (gaSelectedTemplate && activeGlAccountTemplate) {
            for (const req of GL_ACCOUNT_UPLOAD_REQUIRED_HEADERS) {
                const ok = glAccountTemplateColumnRows.some(
                    (row) => row.canonicalKey === req && row.matched
                );
                if (!ok) {
                    return `Map and match all of: ${GL_ACCOUNT_UPLOAD_REQUIRED_HEADERS.join(', ')}. Missing or unmatched: ${req}.`;
                }
            }
            return null;
        }
        const missing = GL_ACCOUNT_UPLOAD_REQUIRED_HEADERS.filter((k) => !raw.has(k));
        if (missing.length > 0) {
            return `Row 1 must include: ${missing.join(', ')}`;
        }
        return null;
    }, [
        uploadFile,
        uploadHeadersLoading,
        uploadFileHeaders,
        gaCombinationPathActive,
        gaSelectedTemplate,
        activeGlAccountTemplate,
        glAccountTemplateColumnRows,
    ]);

    const visibleGlAccountTemplateColumnRows = useMemo(() => {
        if (showMissingTemplateFields) return glAccountTemplateColumnRows.filter((row) => !row.matched);
        return glAccountTemplateColumnRows.filter((row) => row.matched);
    }, [glAccountTemplateColumnRows, showMissingTemplateFields]);

    useEffect(() => {
        if (!uploadFile) {
            setUploadFileHeaders([]);
            setUploadColumnCombination([]);
            setUploadSelectedSourceColumn(null);
            setUploadHeadersLoading(false);
            return undefined;
        }

        let cancelled = false;
        setUploadHeadersLoading(true);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                if (cancelled) return;
                const headers = extractSpreadsheetColumnHeaders(uploadFile, e.target.result);
                setUploadFileHeaders(headers);
                setUploadColumnCombination([]);
                setUploadSelectedSourceColumn(null);
            } catch (err) {
                if (!cancelled) {
                    showToast(err.message || 'Could not read file headers', 'error');
                    setUploadFile(null);
                }
            } finally {
                if (!cancelled) {
                    setUploadHeadersLoading(false);
                }
            }
        };
        reader.onerror = () => {
            if (!cancelled) {
                setUploadHeadersLoading(false);
                showToast('Failed to read file', 'error');
                setUploadFile(null);
            }
        };
        reader.readAsArrayBuffer(uploadFile);
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- showToast stable for this effect
    }, [uploadFile]);

    useEffect(() => {
        if (uploadSystems.length === 0) {
            return;
        }
        if (uploadFileHeaders.length === 0) {
            setUploadColumnCombination([]);
            setUploadSelectedSourceColumn(null);
            return;
        }
        if (!uploadGlSystem?.trim()) {
            setUploadColumnCombination([]);
            setUploadSelectedSourceColumn(null);
            return;
        }
        const system = findUploadSystemRow(uploadSystems, uploadGlSystem);
        const sk = system?.surrogateKey ?? system?.surrogate_key;
        if (!sk || String(sk).trim() === '') {
            setUploadColumnCombination([]);
            setUploadSelectedSourceColumn(null);
            return;
        }
        const combo = resolveUploadCombinationFromSurrogateKey(sk, uploadFileHeaders);
        if (!combo) {
            setUploadColumnCombination([]);
            setUploadSelectedSourceColumn(null);
            return;
        }
        setUploadColumnCombination((prev) => {
            const same =
                prev.length === combo.length &&
                prev.every((p, i) => p.index === combo[i].index);
            if (same) return prev;
            return combo;
        });
        setUploadSelectedSourceColumn(null);
    }, [uploadGlSystem, uploadFileHeaders, uploadSystems]);

    const getUploadAuthToken = () =>
        localStorage.getItem('token') || localStorage.getItem('authToken');

    const fetchUploadDocumentTypes = async () => {
        setLoadingUploadDocumentTypes(true);
        try {
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_id');

            if (!tenantId) {
                setError('Tenant ID not found in local storage');
                setLoadingUploadDocumentTypes(false);
                return;
            }

            const url = new URL(
                `${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}?category=GL`
            );

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                setError(`Failed to load document types: ${response.status} - ${errorText}`);
                setLoadingUploadDocumentTypes(false);
                return;
            }

            const data = await response.json();

            if (!data || data.length === 0) {
                setError('Please configure GL document types for this tenant');
                setUploadDocumentTypes([]);
                setLoadingUploadDocumentTypes(false);
                return;
            }

            const GL_ACCOUNT_DOC_TYPE_NAME = 'GL Account';
            const formattedData = data
                .filter(
                    (item) =>
                        String(item.documentTypeName || '').trim() === GL_ACCOUNT_DOC_TYPE_NAME
                )
                .map((item) => ({
                    id: item.documentTypeId,
                    name: item.documentTypeName,
                    requireFund: false,
                    requireInvestor: false,
                    isMultiInvestor: false,
                    integrationId: null,
                    parserType: 'DOC_PARSER',
                }));

            setUploadDocumentTypes(formattedData);
            if (formattedData.length > 0) {
                setSelectedUploadDocumentType(formattedData[0].id);
            } else {
                setSelectedUploadDocumentType('');
                setError(
                    'GL Account document type is not configured for this tenant. Add it under GL document type tenant settings.'
                );
            }
        } catch (err) {
            console.error('Error fetching document types:', err);
            setError('Failed to fetch document types. Please try again.');
        } finally {
            setLoadingUploadDocumentTypes(false);
        }
    };

    const fetchUploadSystems = async () => {
        setLoadingUploadSystems(true);
        try {
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_id');
            if (!token) {
                setError('No authentication token found');
                return;
            }
            if (!tenantId) {
                setError('Tenant ID not found. Please sign in again.');
                return;
            }

            const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/tenant-systems/by-tenant`);
            url.searchParams.set('tenant_id', tenantId);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch tenant systems: ${response.status}`);
            }

            const data = await response.json();
            const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
            setUploadSystems(list);
        } catch (err) {
            console.error('Error fetching systems:', err);
            setError(`Failed to load systems: ${err.message}`);
        } finally {
            setLoadingUploadSystems(false);
        }
    };

    const fetchUploadFunds = async () => {
        setLoadingUploadFunds(true);
        try {
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_id');
            if (!token || !tenantId) {
                setUploadFunds([]);
                return;
            }
            // Same endpoint + URL shape as Master Data funds tab
            const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');
            const url = `${baseUrl}/conductor/funds/by-tenant-entity?tenantId=${encodeURIComponent(tenantId)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch funds: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            const list = (Array.isArray(data) ? data : [])
                .filter((item) => item.fundName || item.fundCode)
                .map((item) => {
                    const id = item.id || item.masterFundId || null;
                    return {
                        ...item,
                        id,
                        fundName: item.fundName || '',
                        fundCode: item.fundCode || '',
                        isMultiInvestor: item.isMultiInvestor === true || item.isMultiInvestor === 'true',
                    };
                })
                .filter((item) => item.id)
                .sort((a, b) => String(a.fundName || '').localeCompare(String(b.fundName || '')));
            setUploadFunds(list);
        } catch (err) {
            console.error('Error fetching funds:', err);
            setUploadFunds([]);
            showToast(`Failed to load funds: ${err.message}`, 'error');
        } finally {
            setLoadingUploadFunds(false);
        }
    };

    useEffect(() => {
        fetchUploadSystems();
        fetchUploadDocumentTypes();
    }, []);

    // Load funds when the upload dialog opens (same API as Master Data)
    useEffect(() => {
        if (uploadDialogOpen) {
            fetchUploadFunds();
        }
    }, [uploadDialogOpen]);

    const handleUploadDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setUploadDragActive(true);
    };

    const handleUploadDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setUploadDragActive(false);
    };

    const handleUploadDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleUploadDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setUploadDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            const validTypes = ['.csv', '.xls', '.xlsx'];
            const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

            if (validTypes.includes(fileExtension)) {
                setUploadFile(file);
            } else {
                setUploadError('Invalid file type. Please upload a CSV or Excel file.');
            }
        }
    };

    const createUploadUnitOfWork = async (file) => {
        setUploadCurrentStep('Creating upload session...');
        try {
            const token = getUploadAuthToken();
            const tenantId = localStorage.getItem('tenant_id');
            const userId = localStorage.getItem('user_id');

            const selectedFundId = uploadSelectedFund?.id
                ? String(uploadSelectedFund.id)
                : null;
            const selectedFundCode = uploadSelectedFund?.fundCode
                ? String(uploadSelectedFund.fundCode)
                : null;
            const selectedFundName = uploadSelectedFund?.fundName
                ? String(uploadSelectedFund.fundName)
                : null;
            const fundAdditionalDetails =
                selectedFundId || selectedFundCode || selectedFundName
                    ? {
                          fund_id: selectedFundId,
                          fund_code: selectedFundCode,
                          fund_name: selectedFundName,
                      }
                    : null;

            const payload = {
                name: 'GL Accounts Upload',
                documents: [
                    {
                        name: file.name,
                        content_type: file.type || 'application/octet-stream',
                        document_type_id: selectedUploadDocumentType,
                        source_general_ledger: uploadGlSystem ? `${uploadGlSystem.toUpperCase()}_GL` : '',
                        system_id: uploadGlSystem,
                        fund_id: selectedFundId,
                        investor_id: null,
                        holding_id: null,
                        // documents table only has fund_id; name/code persist via additional_details
                        additional_details: fundAdditionalDetails,
                    },
                ],
                tenant_id: tenantId,
                created_by: userId,
            };

            const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
            const apiUrl = `${baseUrl}/${tenantId}/units-of-work`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to create upload session: ${errorText}`);
            }

            const responseData = await response.json();
            setUnitOfWorkId(responseData.id);

            try {
                await AuditLoggers.glMappingFileUploaded(file.name, uploadGlSystem, selectedUploadDocumentType, {
                    unitOfWorkId: responseData.id,
                    documentTypeId: selectedUploadDocumentType,
                    sourceGL: uploadGlSystem,
                });
            } catch (logError) {
                console.error('Error logging GL mapping file upload:', logError);
            }
            return responseData;
        } catch (err) {
            console.error('Error creating unit of work:', err);
            setUploadError(`Failed to create upload session: ${err.message}`);
            throw err;
        }
    };

    const completeMultipartUpload = async (fileMeta, parts) => {
        try {
            const token = getUploadAuthToken();
            const response = await fetch(`${API_BASE}/multipart/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    id: fileMeta.id,
                    unit_of_work_id: fileMeta.unit_of_work_id,
                    location: fileMeta.location,
                    upload_id: fileMeta.upload_id,
                    name: fileMeta.name,
                    content_type: fileMeta.content_type,
                    parts,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Failed to complete upload: ${response.status} ${response.statusText} - ${errorText}`
                );
            }

            return await response.json();
        } catch (err) {
            console.error('Error completing upload:', err);
            setUploadError(`Failed to complete upload: ${err.message}`);
            throw err;
        }
    };

    const getPresignedUrls = async (file, uowId, fileMeta) => {
        setUploadCurrentStep('Preparing upload...');
        const totalParts = Math.ceil(file.size / (5 * 1024 * 1024));

        try {
            const token = getUploadAuthToken();
            const tenantId = localStorage.getItem('tenant_id');
            const apiUrl = `${API_BASE}/multipart/urls`;

            const payload = {
                id: fileMeta.id,
                tenant_id: tenantId,
                unit_of_work_id: uowId,
                location: fileMeta.location,
                upload_id: fileMeta.upload_id,
                name: file.name,
                content_type: file.type || 'application/octet-stream',
                total_parts: totalParts,
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Failed to get upload URLs: ${response.status} ${response.statusText} - ${errorText}`
                );
            }

            return await response.json();
        } catch (err) {
            console.error('Error getting presigned URLs:', err);
            setUploadError(`Failed to prepare upload: ${err.message}`);
            throw err;
        }
    };

    const uploadPart = async (url, partData, partNumber) => {
        try {
            const response = await fetch(url, {
                method: 'PUT',
                body: partData,
            });

            if (!response.ok) {
                throw new Error(`Failed to upload part ${partNumber}`);
            }

            return response.headers.get('ETag');
        } catch (err) {
            console.error(`Error uploading part ${partNumber}:`, err);
            throw err;
        }
    };

    const formatChartOfAccountBulkToast = (body) => {
        if (!body || typeof body !== 'object') {
            return 'Chart of accounts processed.';
        }
        if (typeof body.message === 'string' && body.message.trim()) {
            return body.message.trim();
        }
        const created = body.createdCount ?? 0;
        const updated = body.updatedCount ?? 0;
        const skipped = body.skippedCount ?? 0;
        const saved = body.savedCount ?? created + updated;
        return `Saved ${saved} (${created} created, ${updated} updated); skipped ${skipped}.`;
    };

    const buildChartOfAccountBulkRows = (parsedRows, tenantId, glSystemName, glHeaderId, propertyId, fundId = null) => {
        const glSys =
            normalizeGlSystemQueryParam(glSystemName) || String(glSystemName || '').trim().toUpperCase();
        const withOptional = (out, key, value) => {
            if (value == null) return;
            if (typeof value === 'string' && value.trim() === '') return;
            out[key] = value;
        };
        return parsedRows.map((row) => {
            const glCode1 = String(row.glCode1 ?? row.glCode ?? '').trim();
            const glCode2Raw = String(row.glCode2 ?? '').trim();
            const glCode2 = glCode2Raw || null;
            const glCode =
                glCode1 && glCode2Raw ? `${glCode1}.${glCode2Raw}` : (glCode1 || glCode2Raw || String(row.glCode || '').trim());
            const glDescription = String(row.glDescription ?? '').trim();
            const glAccount = String(row.glAccount ?? '').trim() || glCode;
            const accountTypeTrim = String(row.accountType ?? '').trim();
            const out = {
                tenantId,
                glHeaderId,
                propertyId,
                fundId: fundId || null,
                glCode1,
                glCode2,
                glCode,
                glDescription,
                glAccount,
                glSystem: glSys,
                systemType: 'GL_SYNC',
            };
            if (accountTypeTrim) {
                out.accountType = accountTypeTrim;
            }
            withOptional(out, 'accountSubType', row.accountSubType);
            withOptional(out, 'normalBalance', row.normalBalance);
            withOptional(out, 'groupCode', row.groupCode);
            withOptional(out, 'currency', row.currency);
            withOptional(out, 'transactionType', row.transactionType);
            withOptional(out, 'glJoinField', row.glJoinField);
            if (
                row.additionalAttributes &&
                typeof row.additionalAttributes === 'object' &&
                Object.keys(row.additionalAttributes).length > 0
            ) {
                out.additionalAttributes = row.additionalAttributes;
            }
            return out;
        });
    };

    const parseFileForGlAccountsUpload = (file, glSystem, combinationPair) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            const requiredHeaders = ['GL_CODE', 'GL_DESCRIPTION', 'GL_ACCOUNT'];
            const useCombination =
                Array.isArray(combinationPair) &&
                combinationPair.length >= GL_UPLOAD_COMBINATION_MIN &&
                combinationPair.length <= GL_UPLOAD_COMBINATION_MAX;

            reader.onload = async (event) => {
                try {
                    const data = event.target.result;
                    let parsedData = [];

                    const mapDataRow = (
                        row,
                        headerRow,
                        glCode1Index,
                        glCode2Index,
                        glDescriptionIndex,
                        glAccountIndex,
                        headerIndexMap
                    ) => {
                        const glCode1 = (row[glCode1Index] || '').toString().trim();
                        const glCode2 =
                            glCode2Index >= 0 ? (row[glCode2Index] || '').toString().trim() : '';
                        const glDescription = (row[glDescriptionIndex] || '').toString().trim();
                        const glAccount = (row[glAccountIndex] || '').toString().trim();
                        const optionalByIdx = (headerIndexMap, aliases) => {
                            const idx = aliases
                                .map((a) => headerIndexMap[String(a).trim().toUpperCase()])
                                .find((v) => v != null && v >= 0);
                            if (idx == null || idx < 0) return null;
                            const raw = row[idx];
                            if (raw == null) return null;
                            const s = String(raw).trim();
                            return s === '' ? null : s;
                        };
                        const base = {
                            glCode1,
                            glCode2,
                            glDescription,
                            glAccount,
                            ...(glCode2 ? { accountType: glCode2 } : {}),
                            ...(headerIndexMap
                                ? {
                                    accountSubType: optionalByIdx(headerIndexMap, ['ACCOUNT_SUB_TYPE']),
                                    normalBalance: optionalByIdx(headerIndexMap, ['NORMAL_BALANCE']),
                                    groupCode: optionalByIdx(headerIndexMap, ['GROUP_CODE']),
                                    currency: optionalByIdx(headerIndexMap, ['CURRENCY']),
                                    accountType: optionalByIdx(headerIndexMap, ['ACCOUNT_TYPE']),
                                    transactionType: optionalByIdx(headerIndexMap, ['TRANSACTION_TYPE']),
                                    glJoinField: optionalByIdx(headerIndexMap, ['GL_JOIN_FIELD']),
                                }
                                : {}),
                            glSystem: glSystem.toUpperCase(),
                            glSystemVersion: '1.0',
                        };
                        if (headerIndexMap && Array.isArray(headerRow) && headerRow.length) {
                            const addl = buildAdditionalAttributesFromHeaderIndexRow(row, headerRow, headerIndexMap);
                            if (addl) base.additionalAttributes = addl;
                        }
                        return base;
                    };

                    const resolveHeaderIndices = (headerRow) => {
                        const missingHeaders = requiredHeaders.filter((header) => !headerRow.includes(header));
                        if (missingHeaders.length > 0) {
                            throw new Error(
                                `Missing required headers: ${missingHeaders.join(', ')}. Headers must match exactly (case-sensitive).`
                            );
                        }
                        const glCode1Index = headerRow.indexOf('GL_CODE');
                        const glDescriptionIndex = headerRow.indexOf('GL_DESCRIPTION');
                        const glAccountIndex = headerRow.indexOf('GL_ACCOUNT');
                        const glCode2Index = headerRow.findIndex(
                            (h) => String(h || '').trim().toUpperCase() === 'TRANSACTION_TYPE'
                        );
                        return { glCode1Index, glCode2Index, glDescriptionIndex, glAccountIndex };
                    };

                    const buildHeaderIndexMap = (headerRow) => {
                        const map = {};
                        headerRow.forEach((h, idx) => {
                            const key = String(h || '').trim().toUpperCase();
                            if (key && map[key] == null) map[key] = idx;
                        });
                        return map;
                    };

                    const joinSecondSegmentFromRow = (row, restIndices) =>
                        restIndices
                            .map((idx) => (row[idx] || '').toString().trim())
                            .filter(Boolean)
                            .join('.');

                    const mapCombinationRows = (bodyRows, headerRow) => {
                        const glDescriptionIndex = headerRow.indexOf('GL_DESCRIPTION');
                        const glAccountIndex = headerRow.indexOf('GL_ACCOUNT');
                        const transactionTypeIndex = headerRow.findIndex(
                            (h) => String(h || '').trim().toUpperCase() === 'TRANSACTION_TYPE'
                        );
                        const headerIndexMap = buildHeaderIndexMap(headerRow);
                        const optionalByIdx = (row, aliases) => {
                            const idx = aliases
                                .map((a) => headerIndexMap[String(a).trim().toUpperCase()])
                                .find((v) => v != null && v >= 0);
                            if (idx == null || idx < 0) return null;
                            const raw = row[idx];
                            if (raw == null) return null;
                            const s = String(raw).trim();
                            return s === '' ? null : s;
                        };
                        if (glDescriptionIndex < 0 || glAccountIndex < 0) {
                            throw new Error(
                                'GL_DESCRIPTION and GL_ACCOUNT columns are required in the file when using combination mapping (same as before for description and account).'
                            );
                        }
                        const indices = combinationPair.map((c) => c.index);
                        const i0 = indices[0];
                        const rest = indices.slice(1);
                        const colCount = headerRow.length;
                        const maxIdx = Math.max(...indices, glDescriptionIndex, glAccountIndex);
                        if (indices.some((i) => i < 0) || maxIdx >= colCount) {
                            throw new Error('Mapped column index is outside the file header row.');
                        }
                        const uniq = new Set(indices);
                        if (uniq.size !== indices.length) {
                            throw new Error('Combination columns must be distinct.');
                        }
                        const accountTypeIndex = headerRow.findIndex(
                            (h) => String(h || '').trim().toUpperCase() === 'ACCOUNT_TYPE'
                        );
                        const consumedIndices = new Set(indices);
                        consumedIndices.add(glDescriptionIndex);
                        consumedIndices.add(glAccountIndex);
                        if (transactionTypeIndex >= 0) consumedIndices.add(transactionTypeIndex);
                        if (accountTypeIndex >= 0) consumedIndices.add(accountTypeIndex);
                        ['ACCOUNT_SUB_TYPE', 'NORMAL_BALANCE', 'GROUP_CODE', 'CURRENCY', 'GL_JOIN_FIELD'].forEach(
                            (alias) => {
                                const ix = headerIndexMap[alias];
                                if (ix != null && ix >= 0) consumedIndices.add(ix);
                            }
                        );
                        return bodyRows.map((row) => {
                            const ttRaw =
                                transactionTypeIndex >= 0
                                    ? (row[transactionTypeIndex] || '').toString().trim()
                                    : '';
                            const base = {
                                glCode1: (row[i0] || '').toString().trim(),
                                glCode2: joinSecondSegmentFromRow(row, rest),
                                glDescription: (row[glDescriptionIndex] || '').toString().trim(),
                                glAccount: (row[glAccountIndex] || '').toString().trim(),
                                accountSubType: optionalByIdx(row, ['ACCOUNT_SUB_TYPE']),
                                normalBalance: optionalByIdx(row, ['NORMAL_BALANCE']),
                                groupCode: optionalByIdx(row, ['GROUP_CODE']),
                                currency: optionalByIdx(row, ['CURRENCY']),
                                accountType: optionalByIdx(row, ['ACCOUNT_TYPE']),
                                transactionType: optionalByIdx(row, ['TRANSACTION_TYPE']) || (ttRaw || null),
                                glJoinField: optionalByIdx(row, ['GL_JOIN_FIELD']),
                                glSystem: glSystem.toUpperCase(),
                                glSystemVersion: '1.0',
                            };
                            const addl = buildAdditionalAttributesFromCombinationRow(row, headerRow, consumedIndices);
                            if (addl) base.additionalAttributes = addl;
                            return base;
                        });
                    };

                    if (file.name.endsWith('.csv')) {
                        const text = new TextDecoder().decode(data);
                        const rows = text.split('\n').map((row) => {
                            const regex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
                            return row.split(regex).map((cell) => cell.replace(/^"|"$/g, '').trim());
                        });

                        const headerRow = rows[0] || [];
                        const bodyRows = rows
                            .slice(1)
                            .filter((row) => row.length >= 1 && row.some((cell) => cell.trim() !== ''));

                        if (useCombination) {
                            parsedData = mapCombinationRows(bodyRows, headerRow);
                        } else {
                            const headerIndexMap = buildHeaderIndexMap(headerRow);
                            const { glCode1Index, glCode2Index, glDescriptionIndex, glAccountIndex } =
                                resolveHeaderIndices(headerRow);
                            parsedData = bodyRows.map((row) =>
                                mapDataRow(
                                    row,
                                    headerRow,
                                    glCode1Index,
                                    glCode2Index,
                                    glDescriptionIndex,
                                    glAccountIndex,
                                    headerIndexMap
                                )
                            );
                        }
                    } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
                        const workbook = XLSX.read(data, { type: 'array' });
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];
                        const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                        if (json.length === 0) {
                            throw new Error('The Excel file is empty.');
                        }

                        const headerRow = json[0].map((header) => (header || '').toString());
                        const bodyRows = json.slice(1).filter((row) => row && row.length > 0);

                        if (useCombination) {
                            parsedData = mapCombinationRows(bodyRows, headerRow);
                        } else {
                            const headerIndexMap = buildHeaderIndexMap(headerRow);
                            const { glCode1Index, glCode2Index, glDescriptionIndex, glAccountIndex } =
                                resolveHeaderIndices(headerRow);
                            parsedData = bodyRows.map((row) =>
                                mapDataRow(
                                    row,
                                    headerRow,
                                    glCode1Index,
                                    glCode2Index,
                                    glDescriptionIndex,
                                    glAccountIndex,
                                    headerIndexMap
                                )
                            );
                        }
                    } else {
                        throw new Error('Unsupported file type. Please upload a .csv, .xls, or .xlsx file.');
                    }

                    if (parsedData.length === 0) {
                        throw new Error('No valid data found in the file. Please check the file contents.');
                    }

                    resolve(parsedData);
                } catch (parseErr) {
                    reject(parseErr);
                }
            };

            reader.onerror = (err) => {
                reject(new Error(`Error reading file: ${err.message}`));
            };

            if (file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
                reader.readAsArrayBuffer(file);
            } else {
                reject(new Error('Unsupported file type. Please upload a .csv, .xls, or .xlsx file.'));
            }
        });
    };

    const handleBulkSaveGlAccountsUpload = async (file, glSystem) => {
        setUploading(true);
        setUploadProgress(0);
        setUploadError(null);
        setUploadCurrentStep('Validating file...');

        try {
            const token = getUploadAuthToken();
            const tenantId = localStorage.getItem('tenant_id');
            const userId = localStorage.getItem('user_id');

            if (!tenantId) {
                throw new Error('Tenant ID not found. Please sign in again.');
            }
            if (!selectedUploadDocumentType) {
                throw new Error('Please select a document type.');
            }
            const rawPropertyId =
                localStorage.getItem('property_id') || localStorage.getItem('selected_property_id') || '';
            let propertyIdForApi =
                rawPropertyId && String(rawPropertyId).trim() !== '' ? String(rawPropertyId).trim() : null;
            if (!propertyIdForApi) {
                propertyIdForApi = await fetchDefaultCashReconPropertyId(tenantId, token);
            }

            setUploadCurrentStep('Processing file for GL accounts...');
            if (glAccountUploadMandatoryMessage) {
                throw new Error(glAccountUploadMandatoryMessage);
            }
            const combinationForParse =
                uploadColumnCombination.length >= GL_UPLOAD_COMBINATION_MIN &&
                    uploadColumnCombination.length <= GL_UPLOAD_COMBINATION_MAX
                    ? uploadColumnCombination
                    : null;
            const useCombinationPath = Boolean(combinationForParse);

            let parsedData;
            const useRefTemplateUpload =
                Boolean(gaSelectedTemplate) &&
                !useCombinationPath &&
                gaTemplates.some((t) => t.id === gaSelectedTemplate);

            if (useRefTemplateUpload) {
                const tmpl = activeGlAccountTemplate;
                if (!tmpl) {
                    throw new Error('Selected template is not available. Choose a template again.');
                }
                const activeFieldCount = Object.values(tmpl.mappings || {}).filter(
                    (spec) => spec && spec.isActive !== false
                ).length;
                if (!activeFieldCount) {
                    throw new Error(
                        'The selected template has no active fields. Pick another template or use canonical headers.'
                    );
                }
                for (const [templateKeyRaw, spec] of Object.entries(tmpl.mappings || {})) {
                    const templateKey = String(templateKeyRaw).trim();
                    if (!spec || spec.isActive === false) continue;
                    const pick = gaExcelColumnByTemplateField[templateKey];
                    if (!pick || String(pick).trim() === '') {
                        throw new Error(
                            `Pick a spreadsheet column for template field "${templateKey}" (Excel header "${String(spec.targetColumn || templateKey).trim()}").`
                        );
                    }
                }
                const { data: sheetRows } = await extractGlAccountFieldsFromSheet(file);
                if (!Array.isArray(sheetRows) || sheetRows.length === 0) {
                    throw new Error('No data rows found in the spreadsheet.');
                }
                const mapped = applyGlAccountRefTemplateToRows(
                    sheetRows,
                    tmpl,
                    gaExcelColumnByTemplateField
                );
                const excelHeadersLower = getTemplateMappedExcelHeadersLower(tmpl, gaExcelColumnByTemplateField);
                const canonicalUpper = getTemplateMappedCanonicalUpperSet(
                    tmpl,
                    gaExcelColumnByTemplateField,
                    resolveGlAccountTemplateFieldCanonical
                );
                parsedData = mapped.map((row) => {
                    const base = glAccountMappedObjectsToParsedRows([row], glSystem)[0];
                    const addl = buildAdditionalAttributesFromTemplateRow(row, excelHeadersLower, canonicalUpper);
                    return addl ? { ...base, additionalAttributes: addl } : base;
                });
                const badIdx = parsedData.findIndex(
                    (pr) =>
                        !String(pr.glAccount || '').trim() ||
                        !String(pr.glDescription || '').trim() ||
                        !(String(pr.glCode1 ?? '').trim() || String(pr.glCode2 ?? '').trim())
                );
                if (badIdx !== -1) {
                    throw new Error(
                        `After template mapping, row ${badIdx + 1} is missing GL code, GL description, or GL account. Adjust the template mapping or spreadsheet.`
                    );
                }
            } else {
                parsedData = await parseFileForGlAccountsUpload(file, glSystem, combinationForParse);
            }
            const rowsRaw = Array.isArray(parsedData) ? parsedData : [parsedData];
            const rows = rowsRaw.map(compactGlAccountRowForUpload);
            const fileNameLower = String(file?.name || '').toLowerCase();
            const isExcelFile = fileNameLower.endsWith('.xlsx') || fileNameLower.endsWith('.xls');
            const isCsvFile = fileNameLower.endsWith('.csv');
            const isSpreadsheetFile = isExcelFile || isCsvFile;
            let fileForUpload = file;
            // Keep existing parsing logic (template / surrogate / canonical) but always upload mapped JSON for CSV/Excel.
            if (isSpreadsheetFile) {
                const rowsForS3 = rows.map(buildGlAccountRowForS3NativeKeys);
                const mappedJson = JSON.stringify(rowsForS3, null, 2);
                const jsonBlob = new Blob([mappedJson], { type: 'application/json' });
                const jsonFileName = file.name.replace(/\.(xlsx|xls|csv)$/i, '.json');
                fileForUpload = new File([jsonBlob], jsonFileName, { type: 'application/json' });
            }

            setUploadCurrentStep('Starting file upload...');
            const uowData = await createUploadUnitOfWork(fileForUpload);

            if (!uowData.documents || !Array.isArray(uowData.documents)) {
                throw new Error('Invalid unit of work response: missing documents array');
            }

            const fileMeta = uowData.documents.find((doc) => doc?.name === fileForUpload.name);
            if (!fileMeta) {
                throw new Error(
                    `File metadata not found in unit of work response. Looking for: ${fileForUpload.name}`
                );
            }

            const urlsData = await getPresignedUrls(fileForUpload, uowData.id, fileMeta);

            const totalParts = Math.ceil(fileForUpload.size / (5 * 1024 * 1024));
            const parts = [];

            for (let i = 0; i < totalParts; i++) {
                const start = i * (5 * 1024 * 1024);
                const end = Math.min(start + (5 * 1024 * 1024), fileForUpload.size);
                const partBlob = fileForUpload.slice(start, end);
                const partNumber = i + 1;

                const urlObj = urlsData.urls.find((p) => p.part_number === partNumber);
                if (!urlObj) {
                    throw new Error(`No URL found for part ${partNumber}`);
                }

                setUploadCurrentStep(`Uploading part ${partNumber} of ${totalParts}...`);
                const etag = await uploadPart(urlObj.url, partBlob, partNumber);
                parts.push({ etag, part_number: partNumber });

                const progress = Math.round(((i + 1) / totalParts) * 100);
                setUploadProgress(progress);
            }

            setUploadCurrentStep('Finalizing upload...');
            const uploadResult = await completeMultipartUpload(fileMeta, parts);

            const documentId = uploadResult?.id;
            const fileName = uploadResult?.name ?? fileForUpload.name;
            if (!documentId) {
                throw new Error('Upload completed but no document id was returned.');
            }

            const selectedDoc = uploadDocumentTypes.find((d) => d.id === selectedUploadDocumentType);
            const documentTypeLabel = selectedDoc?.name || String(selectedUploadDocumentType);
            const notesTrimmed = uploadHeaderNotes.trim();

            setUploadCurrentStep('Saving GL header...');
            /** Same pattern as tenant-system config: header token(s), e.g. `GL_CODE` or `GL_CODE.TRANSACTION_TYPE`. */
            const uploadSystemRow = findUploadSystemRow(uploadSystems, glSystem);
            const surrogateKeyForHeader = String(
                uploadSystemRow?.surrogateKey ?? uploadSystemRow?.surrogate_key ?? ''
            ).trim() || null;

            const selectedFundId = uploadSelectedFund?.id
                ? String(uploadSelectedFund.id)
                : null;

            const glHeadersPayload = {
                tenantId,
                propertyId: propertyIdForApi,
                fundId: selectedFundId,
                glSystem: toChartOfAccountGlSystem(glSystem) || String(glSystem || '').toUpperCase(),
                targetGlSystem: null,
                glVersion: '1.0',
                systemType: 'GL_SYNC',
                documentType: documentTypeLabel,
                documentTypeId: selectedUploadDocumentType,
                documentId,
                fileName,
                notes: notesTrimmed || null,
                createdBy: userId,
                updatedBy: userId,
                surrogateKey: surrogateKeyForHeader,
            };

            const glHeadersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-headers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(glHeadersPayload),
            });

            const glHeadersBody = await glHeadersRes.json().catch(() => ({}));
            if (!glHeadersRes.ok) {
                throw new Error(
                    glHeadersBody.message ||
                    glHeadersBody.error ||
                    `GL header save failed: ${glHeadersRes.status}`
                );
            }

            const glHeaderId =
                glHeadersBody.glHeaderId ??
                glHeadersBody.id ??
                glHeadersBody.data?.glHeaderId ??
                glHeadersBody.data?.id;
            if (!glHeaderId) {
                throw new Error('GL header saved but the response did not include glHeaderId.');
            }

            setUploadCurrentStep('Saving chart of accounts...');
            const bulkRows = buildChartOfAccountBulkRows(
                rows,
                tenantId,
                glSystem,
                glHeaderId,
                propertyIdForApi,
                selectedFundId
            );

            const bulkRes = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/chart-of-account/bulk`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(bulkRows),
                }
            );

            const bulkBody = await bulkRes.json().catch(() => ({}));
            if (!bulkRes.ok) {
                if (
                    bulkRes.status === 400 &&
                    typeof bulkBody.message === 'string' &&
                    bulkBody.message.includes('Duplicate')
                ) {
                    throw new Error(bulkBody.message);
                }
                throw new Error(
                    bulkBody.message || bulkBody.error || `Chart of accounts save failed: ${bulkRes.status}`
                );
            }

            setUploadProgress(100);
            showToast(formatChartOfAccountBulkToast(bulkBody), 'success');
            setUploadDialogOpen(false);
            setUploadFile(null);
            setUploadGlSystem('');
            setUploadSelectedFund(null);
            setUploadHeaderNotes('');
            setUploadFileHeaders([]);
            setUploadColumnCombination([]);
            setUploadSelectedSourceColumn(null);
            resetGaUploadTemplateUi();
            setSelectedUploadDocumentType(uploadDocumentTypes[0]?.id || '');
            await fetchRecentUploads();
        } catch (err) {
            console.error('Error in handleBulkSaveGlAccountsUpload:', err);

            if (err.message && err.message.includes('Failed to fetch')) {
                setUploadError(
                    'Unable to connect to the server. Please check your internet connection and try again.'
                );
            } else {
                setUploadError(err.message || 'An unexpected error occurred. Please try again.');
            }
        } finally {
            setUploading(false);
            setUploadCurrentStep('');
        }
    };

    const handleUploadRowClick = (params) => {
        // Use full gl_system (e.g. TALLY_GL / EFRONT_GL). Stripping "_GL" breaks
        // cash-recon chart-of-account lookup against Postgres rows from Refresh COA.
        const source = String(params.row?.source || '').trim();
        if (!source || source === 'Unknown') {
            showToast('Could not determine GL system for this upload', 'warning');
            return;
        }
        const glHeaderId = params.row?.glHeaderId;
        if (!glHeaderId) {
            showToast(
                'Chart of accounts needs a GL header for this upload. Rows from file instances only may not include one.',
                'warning'
            );
            return;
        }
        setSelectedUploadRowId(params.row.id);
        setSelectedSystem(source);
        setSelectedGlHeaderId(glHeaderId);
        setAccountsDialogOpen(true);
    };

    const handleCloseAccountsDialog = () => {
        setAccountsDialogOpen(false);
        setSelectedSystem('');
        setSelectedGlHeaderId(null);
        setSelectedUploadRowId(null);
        setAccounts([]);
        setError(null);
    };

    // ✅ Recent Uploads Menu Handlers
    const handleMenuOpen = (event, row) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedUploadRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedUploadRow(null);
    };

    const handleView = async () => {
        console.log('View clicked for:', selectedUploadRow);
        if (!selectedUploadRow) {
            handleMenuClose();
            return;
        }

        setViewerLoading(true);
        setViewerOpen(true);
        handleMenuClose();

        try {
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_integration')?.trim();

            // First, get the workflow ID using the unitOfWorkId
            const workflowResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/unit-of-work/${selectedUploadRow.unitOfWorkId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!workflowResponse.ok) {
                throw new Error('Failed to fetch workflow instance');
            }

            const workflowData = await workflowResponse.json();
            const workflowId = workflowData;

            // Now fetch the file data using the landing-internal-cas-ref API
            const fileResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/gl-sync-reference/?file_id=${selectedUploadRow.integrationId}&workflow_id=${workflowId}&tenant=${tenantId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!fileResponse.ok) {
                throw new Error('Failed to fetch file data');
            }

            const fileData = await fileResponse.json();

            const transformedData = {
                data: fileData.map(item => {
                    const transformedItem = { ...item };

                    if (item.content && typeof item.content === 'string') {
                        try {
                            transformedItem.content = JSON.parse(item.content);
                        } catch (error) {
                            console.error('Error parsing content field:', error);
                        }
                    }

                    Object.keys(transformedItem).forEach(key => {
                        const value = transformedItem[key];
                        if (typeof value === 'string' && value.trim().startsWith('{') && value.trim().endsWith('}')) {
                            try {
                                transformedItem[key] = JSON.parse(value);
                            } catch (error) {
                                // If parsing fails, keep the original string value
                            }
                        }
                    });

                    return transformedItem;
                })
            };

            setViewerData(transformedData);
        } catch (error) {
            console.error('Error fetching file data:', error);
            showToast('Failed to load file data: ' + error.message, 'error');
        } finally {
            setViewerLoading(false);
        }
    };

    const handleCloseViewer = () => {
        setViewerOpen(false);
        setViewerData(null);
    };

    const handleViewData = async () => {
        console.log('View Data clicked for:', selectedUploadRow);
        if (!selectedUploadRow) {
            handleMenuClose();
            return;
        }

        setViewDataLoading(true);
        setViewDataOpen(true);
        handleMenuClose();

        try {
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_integration')?.trim();

            // First, get the workflow ID using the unitOfWorkId
            const workflowResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/unit-of-work/${selectedUploadRow.unitOfWorkId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!workflowResponse.ok) {
                throw new Error('Failed to fetch workflow instance');
            }

            const workflowData = await workflowResponse.json();
            const workflowId = workflowData;

            // Now fetch the file data
            const fileResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/gl-sync-reference/?file_id=${selectedUploadRow.integrationId}&workflow_id=${workflowId}&tenant=${tenantId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!fileResponse.ok) {
                throw new Error('Failed to fetch file data');
            }

            const fileData = await fileResponse.json();

            // Extract only JSON content fields for View Data
            const jsonDataOnly = fileData
                .map(item => {
                    const jsonFields = {};

                    if (item.content && typeof item.content === 'string') {
                        try {
                            jsonFields.content = JSON.parse(item.content);
                        } catch (error) {
                            console.error('Error parsing content field:', error);
                        }
                    }

                    Object.keys(item).forEach(key => {
                        const value = item[key];
                        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                            jsonFields[key] = value;
                        } else if (typeof value === 'string' && value.trim().startsWith('{') && value.trim().endsWith('}')) {
                            try {
                                jsonFields[key] = JSON.parse(value);
                            } catch (error) {
                                // If parsing fails, skip this field
                            }
                        }
                    });

                    return Object.keys(jsonFields).length > 0 ? jsonFields : null;
                })
                .filter(item => item !== null);

            setViewDataData({ data: jsonDataOnly });
        } catch (error) {
            console.error('Error fetching file data:', error);
            showToast('Failed to load file data: ' + error.message, 'error');
        } finally {
            setViewDataLoading(false);
        }
    };

    const handleCloseViewData = () => {
        setViewDataOpen(false);
        setViewDataData(null);
    };

    const handleDeleteUpload = () => {
        if (!selectedUploadRow) {
            showToast('No upload record found to delete', 'error');
            return;
        }

        setDeleteAction('upload');
        setItemToDelete(selectedUploadRow);
        setConfirmDialogOpen(true);
        handleMenuClose();
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');

            if (!token) {
                showToast('Authentication token not found', 'error');
                return;
            }

            if (!itemToDelete) {
                showToast('No upload record found to delete', 'error');
                return;
            }

            if (deleteAction === 'upload') {
                const fileInstanceId = itemToDelete.fileInstanceId ?? itemToDelete.id;
                if (!fileInstanceId) {
                    showToast('No file instance is linked to this row.', 'error');
                    return;
                }
                const deleteUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/delete`;
                console.log('Calling delete API at URL:', deleteUrl);

                const response = await fetch(deleteUrl, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to delete upload record');
                }

                // Remove the deleted upload from the local state
                const updatedUploads = recentUploads.filter(upload => upload.id !== itemToDelete.id);
                setRecentUploads(updatedUploads);

                console.log('Upload record deleted successfully');
                showToast('Upload record deleted successfully', 'success');
            }

            setConfirmDialogOpen(false);
            setDeleteAction(null);
            setItemToDelete(null);

        } catch (error) {
            console.error('Error deleting item:', error);
            showToast('Failed to delete upload record. Please try again.', 'error');
        }
    };

    const cancelDelete = () => {
        setConfirmDialogOpen(false);
        setItemToDelete(null);
        setDeleteAction(null);
    };

    const handleUploadRowInfoClick = async (event, row) => {
        event.stopPropagation();
        setLogsModalSourceRow(row || null);
        const fileInstancePk = row?.fileInstanceId ?? null;
        if (!fileInstancePk) {
            setLogsModalSourceRow(null);
            showToast('No file instance is linked to this upload.', 'warning');
            return;
        }
        try {
            setLogsLoading(true);
            const token = localStorage.getItem('token');
            const tenantId = localStorage.getItem('tenant_id')?.trim();
            if (!token || !tenantId) {
                showToast('Authentication required', 'error');
                return;
            }
            const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
            const auditLogsHref = `${baseUrl}conductor/file-instances/${encodeURIComponent(
                String(fileInstancePk)
            )}/with-audit-logs?tenantId=${encodeURIComponent(tenantId)}`;

            const response = await fetch(auditLogsHref, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });

            const responseText = await response.text();
            if (!response.ok) {
                let detail = `Request failed (${response.status})`;
                if (responseText) {
                    try {
                        const errBody = JSON.parse(responseText);
                        detail =
                            (typeof errBody.message === 'string' && errBody.message) ||
                            (typeof errBody.error === 'string' && errBody.error) ||
                            (typeof errBody.detail === 'string' && errBody.detail) ||
                            detail;
                    } catch {
                        detail =
                            responseText.length > 200 ? `${responseText.slice(0, 200)}…` : responseText;
                    }
                }
                throw new Error(detail);
            }

            let data = {};
            try {
                data = responseText ? JSON.parse(responseText) : {};
            } catch (parseErr) {
                console.error('audit-logs JSON parse:', parseErr, responseText);
                throw new Error('Server returned invalid JSON for audit logs.');
            }

            setDocumentDetails(data.document ?? null);
            setAuditLogs(Array.isArray(data.auditLogs) ? data.auditLogs : []);
            setAuditLogsPaginationModel((p) => ({ ...p, page: 0 }));
            setIsLogsModalOpen(true);
        } catch (err) {
            console.error('Error fetching audit logs:', err);
            showToast(
                err?.message ? String(err.message) : 'Failed to load document details and audit logs',
                'error'
            );
        } finally {
            setLogsLoading(false);
        }
    };

    // ✅ DataGrid Columns
    const uploadColumns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 200
        },
        {
            field: 'source',
            headerName: 'Source GL',
            width: 150,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'fundLabel',
            headerName: 'Fund',
            flex: 1,
            minWidth: 180,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.875rem' }} title={params.value || ''}>
                    {params.value || '—'}
                </Typography>
            ),
        },
        {
            field: 'glVersion',
            headerName: 'Version',
            width: 100,
            align: 'center',
            headerAlign: 'center',
        },
        // {
        //     field: 'destination',
        //     headerName: 'Destination',
        //     width: 150,
        //     align: 'center',
        //     headerAlign: 'center'
        // },
        // {
        //     field: 'date',
        //     headerName: 'Date',
        //     width: 120,
        //     align: 'right',
        //     headerAlign: 'right'
        // },
        {
            field: 'date',
            headerName: 'Date',
            width: 180,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.875rem' }}>
                    {params.value || ''}
                </Typography>
            ),
        },
        {
            field: 'detailsInfo',
            headerName: '',
            width: 48,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Tooltip
                    title={
                        params.row.fileInstanceId ? 'View details' : 'No file instance for this upload'
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            disabled={!params.row.fileInstanceId}
                            onClick={(e) => handleUploadRowInfoClick(e, params.row)}
                            sx={{ color: ht.green }}
                        >
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <IconButton
                    onClick={(e) => handleMenuOpen(e, params.row)}
                    size="small"
                    sx={{
                        color: ht.text,
                        '&:hover': {
                            color: ht.green,
                            backgroundColor: 'rgba(2, 107, 29, 0.1)'
                        }
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    const hasAccountTypeValues = useMemo(
        () => accounts.some((row) => row?.accountType != null && String(row.accountType).trim() !== ''),
        [accounts]
    );

    // ✅ DataGrid Columns for Accounts
    const accountColumns = useMemo(() => [
        {
            field: 'glCode',
            headerName: 'GL Code',
            flex: 1,
            minWidth: 110
        },
        {
            field: 'glDescription',
            headerName: 'Description',
            flex: 2,
            minWidth: 160
        },
        {
            field: 'glAccount',
            headerName: 'Account',
            flex: 1,
            minWidth: 110
        },
        ...(hasAccountTypeValues
            ? [{
                field: 'accountType',
                headerName: 'Account Type',
                flex: 1,
                minWidth: 150,
                valueGetter: (params) => {
                    const v = params?.row?.accountType;
                    return v == null || String(v).trim() === '' ? '' : String(v);
                },
            }]
            : []),
        // {
        //     field: 'actions',
        //     headerName: 'Actions',
        //     width: 88,
        //     minWidth: 72,
        //     sortable: false,
        //     filterable: false,
        //     disableColumnMenu: true,
        //     renderCell: (params) => (
        //         <Box display="flex" gap={1}>
        //             {/* <IconButton
        //                 onClick={() => handleDeleteAccount(params.row)}
        //                 size="small"
        //                 color="error"
        //                 title="Delete Account"
        //             >
        //                 <DeleteIcon fontSize="small" />
        //             </IconButton> */}
        //             <IconButton
        //                 onClick={() => handleDeleteAccount(params.row)}
        //                 size="small"
        //                 color="error"
        //                 title={(!isHarmonateUser && !hasPermission('GL Sync', 'delete')) ? "You don't have permission to delete" : "Delete Account"}
        //                 disabled={!isHarmonateUser && !hasPermission('GL Sync', 'delete')}
        //                 sx={{
        //                     '&.Mui-disabled': {
        //                         color: 'rgba(0, 0, 0, 0.26)',
        //                         '&:hover': {
        //                             backgroundColor: 'transparent'
        //                         }
        //                     }
        //                 }}
        //             >
        //                 <DeleteIcon fontSize="small" />
        //             </IconButton>
        //         </Box>
        //     ),
        // },
    ], [hasAccountTypeValues]);

    return (
        <Box sx={{ ...harmonatePageSx, p: { xs: 2.5, sm: 3.5 } }}>
            {/* Breadcrumb Navigation — match GLSync.jsx (no maxWidth container; full width with p:3 gutters) */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    sx={{ fontSize: '0.9rem', '& .MuiBreadcrumbs-li': { color: ht.white }, '& a': { color: ht.white } }}
                    separator={<NavigateNextIcon fontSize="small" sx={{ color: ht.white }} />}
                >
                    <Link
                        to="/gl-sync"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            color: 'inherit',
                        }}
                    >
                        <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                        GL Sync
                    </Link>
                    <Typography
                        sx={{ display: 'flex', alignItems: 'center', color: ht.white }}
                    >
                        GL Accounts
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2.5,
                    mb: 3,
                }}
            >
                <Typography variant="h6" sx={{ ...harmonatePageTitleSx, mb: 0, fontSize: '1.25rem' }}>
                    Uploads
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 2,
                        width: { xs: '100%', sm: 'auto' },
                    }}
                >
                    <Tooltip title="Load chart of accounts from Snowflake into Postgres (GL Sync)">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={
                                    refreshingCoa ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <CloudDownloadIcon />
                                    )
                                }
                                onClick={() => setCoaConfirmAction('refresh')}
                                disabled={
                                    refreshingCoa ||
                                    pushingCoa ||
                                    (!isHarmonateUser && !hasPermission('GL Sync', 'upload'))
                                }
                                sx={harmonateUploadOutlinedSx}
                            >
                                {refreshingCoa ? 'Refreshing…' : 'Refresh COA'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Push GL Sync chart of accounts from Postgres to Snowflake">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={
                                    pushingCoa ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <CloudUploadIcon />
                                    )
                                }
                                onClick={() => setCoaConfirmAction('push')}
                                disabled={
                                    refreshingCoa ||
                                    pushingCoa ||
                                    (!isHarmonateUser && !hasPermission('GL Sync', 'upload'))
                                }
                                sx={harmonateUploadOutlinedSx}
                            >
                                {pushingCoa ? 'Pushing…' : 'Push COA'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                        disabled={!isHarmonateUser && !hasPermission('GL Sync', 'upload')}
                        sx={harmonateProceedButtonSx}
                    >
                        Upload Files
                    </Button>
                    <Box
                        component="form"
                        autoComplete="off"
                        noValidate
                        onSubmit={(e) => e.preventDefault()}
                        sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            autoComplete="off"
                            type="search"
                            value={uploadSearchQuery}
                            onChange={(e) => setUploadSearchQuery(e.target.value)}
                            placeholder="Search by name, source, fund, version, date…"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={harmonatePageSearchIconSx} />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                autoComplete: 'new-password',
                                'data-form-type': 'other',
                                autoCorrect: 'off',
                                autoCapitalize: 'none',
                                spellCheck: 'false',
                                name: 'gl-accounts-upload-search',
                                inputMode: 'search',
                                'aria-label': 'Search uploads',
                            }}
                            sx={{ width: { xs: '100%', sm: 320 }, ...harmonatePageSearchFieldSx }}
                        />
                    </Box>
                </Box>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    ...harmonateCardSx,
                    bgcolor: ht.white,
                    flex: 1,
                    mb: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {loadingUploads ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 280 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CircularProgress sx={{ color: ht.blue }} />
                            <Typography variant="body1" color="text.secondary">
                                Loading recent uploads...
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <DataGridPro
                        columnResizeMode="onEnd"
                        resizable
                        scrollbarSize={0}
                        rows={filteredUploadRows}
                        columns={uploadColumns}
                        rowHeight={40}
                        onRowClick={handleUploadRowClick}
                        getRowClassName={(params) =>
                            params.row.id == selectedUploadRowId ? 'gl-accounts-upload-row--selected' : ''
                        }
                        sx={{
                            ...harmonateDataGridSx,
                            width: '100%',
                            flex: 1,
                            minHeight: { xs: 'calc(100vh - 220px)', sm: 'calc(100vh - 200px)' },
                            '& .MuiDataGrid-virtualScroller': {
                                overflowX: 'auto',
                                overflowY: 'auto',
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: ht.listBg,
                                cursor: 'pointer',
                            },
                            '& .gl-accounts-upload-row--selected': {
                                bgcolor: 'rgba(46, 166, 160, 0.14)',
                            },
                        }}
                        disableSelectionOnClick
                        pagination
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slots={{
                            noRowsOverlay: () => (
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', px: 2, textAlign: 'center' }}>
                                    <Typography variant="body1" color="text.secondary">
                                        {recentUploads.length === 0
                                            ? 'No recent uploads available'
                                            : uploadSearchQuery.trim()
                                                ? 'No uploads match your search'
                                                : 'No recent uploads available'}
                                    </Typography>
                                </Box>
                            ),
                        }}
                    />
                )}
            </Paper>

            <Dialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false);
                    setUploadHeaderNotes('');
                    setUploadFileHeaders([]);
                    setUploadColumnCombination([]);
                    setUploadSelectedSourceColumn(null);
                    resetGaUploadTemplateUi();
                }}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        ...harmonateDialogPaperProps.sx,
                        maxHeight: 'min(920px, calc(100vh - 32px))',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogTitle sx={{ ...harmonateDialogTitleSx, flexShrink: 0, fontSize: '1.125rem' }}>
                    Upload GL Account
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        ...harmonateDialogContentSx,
                        ...harmonateFormFieldsSx,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                    }}
                >
                    <DialogContentText sx={{ mb: 0, fontSize: '14px' }}>
                        Upload your GL accounts data file and Select a GL system
                    </DialogContentText>

                    <Box>

                        <HiddenInput
                            accept=".csv,.xls,.xlsx"
                            id="gl-accounts-upload-file-input"
                            type="file"
                            onChange={(e) => setUploadFile(e.target.files[0] || null)}
                        />
                        <label htmlFor="gl-accounts-upload-file-input" style={{ display: 'block', marginBottom: '8px' }}>
                            <UploadArea
                                onDragEnter={handleUploadDragEnter}
                                onDragOver={handleUploadDragOver}
                                onDragLeave={handleUploadDragLeave}
                                onDrop={handleUploadDrop}
                                sx={{
                                    padding: '24px 16px',
                                    border: uploadDragActive ? `2px dashed ${ht.green}` : `2px dashed ${ht.line}`,
                                    borderRadius: ht.cardRadius,
                                    backgroundColor: uploadDragActive ? ht.secondaryTab : ht.listBg,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: ht.green,
                                        backgroundColor: ht.secondaryTab,
                                    },
                                }}
                            >
                                <CloudUploadIcon sx={{ fontSize: 32, color: ht.green, mb: 1 }} />
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '13px' }}>
                                    {uploadDragActive ? 'Drop your file here' : 'Drop your file or browse'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: ht.text, fontSize: '11px' }}>
                                    Max 10 MB files are allowed
                                </Typography>
                            </UploadArea>
                        </label>

                        {uploadFile && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    mt: 2,
                                    mb: 0,
                                    backgroundColor: ht.listBg,
                                    border: `1px solid ${ht.line}`,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 1,
                                            backgroundColor: '#e3f2fd',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <InsertDriveFileIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                color: '#000000',
                                                fontSize: '14px',
                                                mb: 0.5,
                                            }}
                                        >
                                            {uploadFile.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#000000', fontSize: '12px' }}>
                                            {(uploadFile.size / 1024).toFixed(2)} KB
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setUploadFile(null);
                                        setUploadProgress(0);
                                        setUploadFileHeaders([]);
                                        setUploadColumnCombination([]);
                                        setUploadSelectedSourceColumn(null);
                                        resetGaUploadTemplateUi();
                                    }}
                                    sx={{
                                        color: ht.text,
                                        '&:hover': { color: ht.red, backgroundColor: 'rgba(239, 63, 63, 0.08)' },
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Paper>
                        )}
                        {uploadFile && !uploadHeadersLoading && glAccountUploadMandatoryMessage ? (
                            <Alert severity="warning" sx={{ mt: 1.5 }}>
                                {glAccountUploadMandatoryMessage}
                            </Alert>
                        ) : null}
                        <Typography variant="caption" sx={{ color: '#000000', fontSize: '10px', display: 'block', mt: 1.5 }}>
                            Only support CSV and XLS/XLSX
                        </Typography>
                        {/* <Typography variant="subtitle2" sx={{ color: '#66BB6A', fontWeight: 600 }}>
                            Upload details
                        </Typography> */}

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                alignItems: 'stretch',
                                mt: 1,
                                mb: 1,
                            }}
                        >
                            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    disabled={loadingUploadSystems}
                                    componentsProps={glAccountsUploadGlSystemAutocompleteComponentsProps}
                                    options={uploadSystems}
                                    getOptionLabel={(option) => {
                                        const name = option?.name != null ? String(option.name) : '';
                                        return name;
                                    }}
                                    value={findUploadSystemRow(uploadSystems, uploadGlSystem) || null}
                                    onChange={(_, newValue) => {
                                        setUploadGlSystem(newValue?.name || '');
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="GL System"
                                            size="small"
                                            placeholder="Select GL system"
                                            sx={{ minWidth: 0 }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingUploadSystems ? (
                                                            <CircularProgress size={20} />
                                                        ) : (
                                                            params.InputProps.endAdornment
                                                        )}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.id != null && value?.id != null
                                            ? option.id === value.id
                                            : option?.name === value?.name
                                    }
                                    sx={{ fontSize: '14px' }}
                                />
                            </FormControl>
                            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    disabled={loadingUploadFunds}
                                    componentsProps={glAccountsUploadGlSystemAutocompleteComponentsProps}
                                    options={uploadFunds}
                                    getOptionLabel={(option) => {
                                        if (!option || typeof option === 'string') return '';
                                        const name = option.fundName || '';
                                        const code = option.fundCode || '';
                                        if (name && code) return `${code} (${name})`;
                                        return code || name || '';
                                    }}
                                    value={uploadSelectedFund}
                                    onChange={(_, newValue) => setUploadSelectedFund(newValue)}
                                    clearOnBlur={false}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Fund (optional)"
                                            size="small"
                                            placeholder="Select fund"
                                            sx={{ minWidth: 0 }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingUploadFunds ? (
                                                            <CircularProgress size={20} />
                                                        ) : (
                                                            params.InputProps.endAdornment
                                                        )}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.id != null && value?.id != null
                                            ? String(option.id) === String(value.id)
                                            : option?.fundCode === value?.fundCode
                                    }
                                    noOptionsText={loadingUploadFunds ? 'Loading funds…' : 'No funds found'}
                                    sx={{ fontSize: '14px' }}
                                />
                            </FormControl>
                            {loadingUploadDocumentTypes ? (
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        py: 1,
                                        minHeight: 40,
                                    }}
                                >
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" sx={{ ml: 1, fontSize: '12px' }}>
                                        Loading document types...
                                    </Typography>
                                </Box>
                            ) : selectedUploadDocumentType ? (
                                <TextField
                                    label="Document Type"
                                    value="GL Account"
                                    size="small"
                                    fullWidth
                                    disabled
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1, minWidth: 0 }}
                                />
                            ) : (
                                <Alert severity="warning" sx={{ flex: 1, py: 0.5 }}>
                                    GL Account is not enabled for this tenant. Configure it in document type settings.
                                </Alert>
                            )}
                        </Box>

                        {selectedUploadDocumentType && uploadGlSystem?.trim() ? (
                            <Box sx={{ mt: 2 }}>
                                {gaLoadingTemplates ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#000000' }}>
                                            Loading ref-templates…
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 0.5,
                                            }}
                                        >
                                            <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
                                                <InputLabel id="gl-accounts-ref-template-label">
                                                    Spreadsheet template (optional)
                                                </InputLabel>
                                                <Select
                                                    labelId="gl-accounts-ref-template-label"
                                                    label="Spreadsheet template (optional)"
                                                    value={gaSelectedTemplate}
                                                    onChange={(e) =>
                                                        setGaSelectedTemplate(String(e.target.value ?? ''))
                                                    }
                                                    disabled={uploading}
                                                >
                                                    <MenuItem value="">
                                                        <em>No template — canonical headers in row 1</em>
                                                    </MenuItem>
                                                    {gaTemplates.map((tpl) => (
                                                        <MenuItem key={tpl.id} value={tpl.id}>
                                                            {tpl.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Tooltip title="Add or edit template fields (same as Reference Templates)">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit reference template mappings"
                                                        disabled={
                                                            uploading ||
                                                            !gaSelectedTemplate ||
                                                            !activeGaApiTemplateForEditor
                                                        }
                                                        onClick={() => setGaTemplateMappingsEditorOpen(true)}
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                        {gaTemplates.length === 0 ? (
                                            <Typography
                                                variant="caption"
                                                sx={{ display: 'block', mt: 0.75, color: '#6c757d', fontSize: '12px' }}
                                            >
                                                No ref-templates returned for GL Account / this system (
                                                <code style={{ fontSize: '11px' }}>by-document-and-gl</code>
                                                ). Use canonical headers.
                                            </Typography>
                                        ) : null}
                                        {gaCombinationPathActive ? (
                                            <Alert severity="info" sx={{ mt: 1, py: 0.75, fontSize: '13px' }}>
                                                Surrogate-key combination columns are active, so upload still follows the existing
                                                combination logic. Template comparison remains visible for reference.
                                            </Alert>
                                        ) : null}
                                        {gaSelectedTemplate &&
                                        uploadFile &&
                                        glAccountTemplateColumnRows.length > 0 ? (
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    mt: 2,
                                                    p: 2,
                                                    border: `1px solid ${ht.line}`,
                                                    borderRadius: 1,
                                                    bgcolor: ht.listBg,
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#374151' }}>
                                                    Template fields vs your file
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: '#6b7280' }}>
                                                Left: template logical field and the Excel header your file must provide (targetColumn). Right: matching column from the uploaded sheet. Green when that header is present; red when it is missing.
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                                        Missing fields: {missingTemplateColumnCount}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() => setShowMissingTemplateFields((prev) => !prev)}
                                                    >
                                                        {showMissingTemplateFields ? 'Show mapped fields' : 'Show missing fields'}
                                                    </Button>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                                        gap: 0,
                                                        border: `1px solid ${ht.line}`,
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        maxHeight: 360,
                                                        overflowY: 'auto',
                                                        bgcolor: '#fff',
                                                    }}
                                                >
                                                    <Box sx={{ px: 1.5, py: 1, bgcolor: ht.listBg, borderBottom: `1px solid ${ht.line}`, fontSize: '12px', fontWeight: 600, color: ht.text }}>
                                                        Template required header
                                                    </Box>
                                                    <Box sx={{ px: 1.5, py: 1, bgcolor: ht.listBg, borderBottom: `1px solid ${ht.line}`, fontSize: '12px', fontWeight: 600, color: ht.text }}>
                                                        Column in your file
                                                    </Box>
                                                    {visibleGlAccountTemplateColumnRows.length === 0 ? (
                                                        <Box
                                                            sx={{
                                                                gridColumn: { xs: '1 / -1', sm: '1 / 3' },
                                                                px: 1.5,
                                                                py: 2,
                                                                fontSize: '13px',
                                                                color: '#6b7280',
                                                            }}
                                                        >
                                                            {showMissingTemplateFields
                                                                ? 'All required template fields are present.'
                                                                : 'No mapped fields found in your file.'}
                                                        </Box>
                                                    ) : null}
                                                    {visibleGlAccountTemplateColumnRows.map((row, idx) => (
                                                        <React.Fragment key={row.templateKey}>
                                                            <Box
                                                                sx={{
                                                                    px: 1.5,
                                                                    py: 1.25,
                                                                    borderBottom:
                                                                        idx < visibleGlAccountTemplateColumnRows.length - 1 ? `1px solid ${ht.line}` : 'none',
                                                                    borderRight: { xs: 'none', sm: `1px solid ${ht.line}` },
                                                                    fontSize: '13px',
                                                                    color: row.matched ? '#166534' : '#b91c1c',
                                                                    bgcolor: row.matched ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.06)',
                                                                }}
                                                            >
                                                                <Typography component="span" sx={{ fontWeight: 600, color: 'inherit' }}>
                                                                    {row.templateKey}
                                                                </Typography>
                                                                <Typography component="span" sx={{ color: '#6b7280', mx: 0.75 }}>
                                                                    →
                                                                </Typography>
                                                                <Typography component="span" sx={{ fontFamily: 'monospace', color: 'inherit' }}>
                                                                    {row.expectedHeader}
                                                                </Typography>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    px: 1.5,
                                                                    py: 1.25,
                                                                    borderBottom:
                                                                        idx < visibleGlAccountTemplateColumnRows.length - 1 ? `1px solid ${ht.line}` : 'none',
                                                                    fontSize: '13px',
                                                                    fontFamily: 'monospace',
                                                                    color: row.matched ? '#166534' : '#b91c1c',
                                                                    bgcolor: row.matched ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.06)',
                                                                }}
                                                            >
                                                                {row.displayFileCol}
                                                            </Box>
                                                        </React.Fragment>
                                                    ))}
                                                </Box>
                                            </Paper>
                                        ) : null}
                                    </>
                                )}
                            </Box>
                        ) : null}

                        {uploadFile && (
                            uploadHeadersLoading ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 2,
                                        justifyContent: 'center',
                                    }}
                                >
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#000000' }}>
                                        Reading column headers…
                                    </Typography>
                                </Box>
                            ) : (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        mt: 2,
                                        mb: 2,
                                        border: `1px solid ${ht.line}`,
                                        borderRadius: 1,
                                        backgroundColor: ht.listBg,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Column mapping (optional)
                                    </Typography>
                                    {/* <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: '#000000' }}>
                                            Choose two columns in order: their cell values are joined with a dot to form
                                            the saved account code (same idea as <strong>GL_CODE</strong> plus optional{' '}
                                            <strong>TRANSACTION_TYPE</strong>). <strong>GL_DESCRIPTION</strong> and{' '}
                                            <strong>GL_ACCOUNT</strong> are always read from those headers in the file.
                                            If you skip this mapping, the account code is taken from the file as before
                                            using <strong>GL_CODE</strong> and optional <strong>TRANSACTION_TYPE</strong>.
                                        </Typography> */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            gap: 2,
                                            alignItems: { xs: 'stretch', sm: 'flex-start' },
                                            minHeight: 160,
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                Source columns
                                            </Typography>
                                            <List
                                                dense
                                                sx={{
                                                    maxHeight: 220,
                                                    overflow: 'auto',
                                                    border: `1px solid ${ht.line}`,
                                                    borderRadius: 1,
                                                    bgcolor: '#fff',
                                                }}
                                            >
                                                {uploadSourceColumnsAvailable.length === 0 ? (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary={
                                                                uploadFileHeaders.length === 0
                                                                    ? 'No headers found'
                                                                    : 'All columns mapped'
                                                            }
                                                        />
                                                    </ListItem>
                                                ) : (
                                                    uploadSourceColumnsAvailable.map((col) => (
                                                        <ListItem key={col.index} disablePadding>
                                                            <ListItemButton
                                                                selected={
                                                                    uploadSelectedSourceColumn?.index ===
                                                                    col.index
                                                                }
                                                                onClick={() =>
                                                                    setUploadSelectedSourceColumn(col)
                                                                }
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        col.name?.trim()
                                                                            ? col.name
                                                                            : `(Column ${col.index + 1})`
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    ))
                                                )}
                                            </List>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                py: { xs: 0, sm: 2 },
                                            }}
                                        >
                                            <IconButton
                                                color="primary"
                                                aria-label="Add selected column to combination"
                                                disabled={
                                                    !uploadSelectedSourceColumn ||
                                                    uploadColumnCombination.length >= GL_UPLOAD_COMBINATION_MAX
                                                }
                                                onClick={() => {
                                                    if (
                                                        !uploadSelectedSourceColumn ||
                                                        uploadColumnCombination.length >= GL_UPLOAD_COMBINATION_MAX
                                                    ) {
                                                        return;
                                                    }
                                                    setUploadColumnCombination((prev) => [
                                                        ...prev,
                                                        uploadSelectedSourceColumn,
                                                    ]);
                                                    setUploadSelectedSourceColumn(null);
                                                }}
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <ArrowForwardIcon />
                                            </IconButton>
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                Combination columns
                                            </Typography>
                                            <List
                                                dense
                                                sx={{
                                                    maxHeight: 220,
                                                    overflow: 'auto',
                                                    border: `1px solid ${ht.line}`,
                                                    borderRadius: 1,
                                                    bgcolor: '#fff',
                                                }}
                                            >
                                                {uploadColumnCombination.length === 0 ? (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="None yet"
                                                            secondary="If the GL system surrogate key uses | between header names that exist in this file (e.g. GL_CODE|TRANSACTION_TYPE), columns auto-fill. Or add 1–5 source columns: first → primary code segment; extras → secondary segment built from those cells."
                                                        />
                                                    </ListItem>
                                                ) : (
                                                    uploadColumnCombination.map((col, idx) => (
                                                        <ListItem
                                                            key={`${col.index}-${idx}`}
                                                            secondaryAction={
                                                                <IconButton
                                                                    edge="end"
                                                                    size="small"
                                                                    aria-label="Remove from combination"
                                                                    onClick={() =>
                                                                        setUploadColumnCombination((prev) =>
                                                                            prev.filter((_, i) => i !== idx)
                                                                        )
                                                                    }
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            }
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    col.name?.trim()
                                                                        ? col.name
                                                                        : `(Column ${col.index + 1})`
                                                                }
                                                            />
                                                        </ListItem>
                                                    ))
                                                )}
                                            </List>
                                        </Box>
                                    </Box>
                                </Paper>
                            )
                        )}


                    </Box>

                    

                    <TextField
                        label="Description"
                        name="uploadHeaderNotes"
                        value={uploadHeaderNotes}
                        onChange={(e) => setUploadHeaderNotes(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={uploading}
                        InputLabelProps={{ shrink: true }}
                        // helperText="Optional notes"
                        FormHelperTextProps={{ sx: { mx: 0 } }}
                    />

                    {uploading && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                {uploadCurrentStep || 'Uploading...'} ({uploadProgress}%)
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                    )}
                    {uploadError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {uploadError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexShrink: 0, px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                        onClick={() => {
                            setUploadDialogOpen(false);
                            setUploadFile(null);
                            setUploadGlSystem('');
                            setUploadSelectedFund(null);
                            setUploadHeaderNotes('');
                            setUploadProgress(0);
                            setUploadError(null);
                            setUploadFileHeaders([]);
                            setUploadColumnCombination([]);
                            setUploadSelectedSourceColumn(null);
                            resetGaUploadTemplateUi();
                        }}
                        sx={{ fontSize: '13px', ...harmonateCancelButtonSx }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (uploadGlSystem && uploadFile && selectedUploadDocumentType) {
                                handleBulkSaveGlAccountsUpload(uploadFile, uploadGlSystem);
                            } else {
                                showToast('Please select a GL system, document type, and upload a file.', 'warning');
                            }
                        }}
                        variant="contained"
                        disabled={
                            !uploadGlSystem ||
                            !uploadFile ||
                            !selectedUploadDocumentType ||
                            uploading ||
                            uploadHeadersLoading ||
                            Boolean(glAccountUploadMandatoryMessage)
                        }
                        sx={harmonateProceedButtonSx}
                    >
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Accounts — modal (same APIs as before) */}
            <Dialog
                open={accountsDialogOpen}
                onClose={handleCloseAccountsDialog}
                maxWidth="lg"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        ...harmonateDialogPaperProps.sx,
                        maxHeight: '92vh',
                        height: '92vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        ...harmonateDialogTitleSx,
                        alignItems: 'flex-start',
                        gap: 2,
                        pr: 1,
                        py: 2,
                        flexShrink: 0,
                    }}
                >
                    <Box>
                        <Typography variant="h6" component="span" sx={{ color: ht.white, fontWeight: 700 }}>
                            Accounts
                        </Typography>
                        {selectedSystem ? (
                            <Typography variant="body2" sx={{ mt: 0.5, color: ht.white }}>
                                GL system: {selectedSystem}
                            </Typography>
                        ) : null}
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseAccountsDialog}
                        size="small"
                        sx={harmonateDialogCloseIconSx}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        ...harmonateDialogContentSx,
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        px: 2.5,
                        pb: 2,
                        overflow: 'hidden',
                    }}
                >
                    {error ? (
                        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                            {error}
                        </Alert>
                    ) : null}

                    <Paper
                        elevation={0}
                        sx={{
                            ...harmonateCardSx,
                            bgcolor: ht.white,
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            border: `1px solid ${ht.line}`,
                        }}
                    >
                        {loadingAccounts ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 320 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                    <CircularProgress />
                                    <Typography variant="body1" color="text.secondary">
                                        Loading accounts...
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ width: '100%', height: { xs: 'calc(92vh - 260px)', sm: 'calc(92vh - 240px)' }, minHeight: 360 }}>
                                <DataGridPro
                                    columnResizeMode="onEnd"
                                    resizable
                                    scrollbarSize={0}
                                    rows={accounts}
                                    columns={accountColumns}
                                    rowHeight={40}
                                    getRowId={(row) => row.glId}
                                    sx={{
                                        ...harmonateDataGridSx,
                                        width: '100%',
                                        height: '100%',
                                        '& .MuiDataGrid-virtualScroller': {
                                            overflowX: 'hidden',
                                            overflowY: 'auto',
                                        },
                                        '& .MuiDataGrid-row:hover': {
                                            bgcolor: ht.listBg,
                                        },
                                    }}
                                    disableSelectionOnClick
                                    pagination
                                    paginationModel={accountsPaginationModel}
                                    onPaginationModelChange={setAccountsPaginationModel}
                                    pageSizeOptions={[25, 50, 100]}
                                    slots={{
                                        noRowsOverlay: () => (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    height: '100%',
                                                    px: 2,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Typography variant="body1" color="text.secondary">
                                                    {`No accounts found for ${selectedSystem}.`}
                                                </Typography>
                                            </Box>
                                        ),
                                    }}
                                />
                            </Box>
                        )}
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
                    <Button onClick={handleCloseAccountsDialog} sx={harmonateCancelButtonSx}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Toast
                open={toastState.open}
                onClose={hideToast}
                message={toastState.message}
                severity={toastState.severity}
                title={toastState.title}
                autoHideDuration={toastState.autoHideDuration}
                position={toastState.position}
                variant={toastState.variant}
            />

            {/* Document Details & Audit Logs — WorkflowDrawer / GLSync parity */}
            {isLogsModalOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1300,
                    }}
                    onClick={() => {
                        setIsLogsModalOpen(false);
                        setDocumentDetails(null);
                        setAuditLogs([]);
                        setLogsModalSourceRow(null);
                        setLogsJsonDialogOpen(false);
                        setLogsJsonData(null);
                        setLogsJsonError(null);
                        setLogsExtractionDialogOpen(false);
                        setLogsExtractionDetails(null);
                    }}
                >
                    <Paper
                        onClick={(e) => e.stopPropagation()}
                        elevation={0}
                        sx={{
                            width: '95%',
                            maxWidth: '1200px',
                            height: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 0,
                            overflow: 'hidden',
                            ...harmonateCardSx,
                            bgcolor: ht.white,
                        }}
                    >
                        <Box
                            sx={{
                                ...harmonateDialogTitleSx,
                                p: 2.5,
                                borderBottom: 'none',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1, color: ht.white }}>
                                    Document Details & Audit Logs
                                </Typography>
                                {/* <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLogsModalSnowflakeClick();
                                    }}
                                    disabled={
                                        logsExtractionLoading ||
                                        !logsModalSourceRow?.fastApiFileInstanceId ||
                                        !logsModalSourceRow?.processId
                                    }
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        py: 0.5,
                                        px: 1.5,
                                        minWidth: '100px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {logsExtractionLoading ? <CircularProgress size={16} /> : 'Snowflake Info'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (logsModalSourceRow) {
                                            handleLogsModalViewRawJson(logsModalSourceRow);
                                        }
                                    }}
                                    disabled={
                                        !logsModalSourceRow?.unitOfWorkId ||
                                        !logsModalSourceRow?.fastApiFileInstanceId ||
                                        !logsModalSourceRow?.processId
                                    }
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        py: 0.5,
                                        px: 1.5,
                                    }}
                                >
                                    Raw JSON
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // eslint-disable-next-line no-console
                                        console.log('Parser Status clicked');
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        py: 0.5,
                                        px: 1.5,
                                    }}
                                >
                                    Parser Status
                                </Button> */}
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setIsLogsModalOpen(false);
                                        setDocumentDetails(null);
                                        setAuditLogs([]);
                                        setLogsModalSourceRow(null);
                                        setLogsJsonDialogOpen(false);
                                        setLogsJsonData(null);
                                        setLogsJsonError(null);
                                        setLogsExtractionDialogOpen(false);
                                        setLogsExtractionDetails(null);
                                    }}
                                    sx={harmonateDialogCloseIconSx}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>

                        {logsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                <CircularProgress size={24} sx={{ color: ht.green }} />
                            </Box>
                        ) : (
                            <Box sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: ht.listBg }}>
                                {documentDetails && (
                                    <Box
                                        sx={{
                                            mb: 3,
                                            p: 2,
                                            bgcolor: ht.white,
                                            borderRadius: ht.cardRadius,
                                            border: `1px solid ${ht.line}`,
                                            boxShadow: ht.cardShadow,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                width: '100%',
                                                mb: 1.5,
                                                pb: 1,
                                                borderBottom: `1px solid ${ht.line}`,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 700, color: ht.text }}
                                            >
                                                DOCUMENT INFORMATION
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                                                gap: 3,
                                                rowGap: 2.5,
                                                '& > div': {
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.75,
                                                },
                                                mb: 0,
                                            }}
                                        >
                                            <div>
                                                <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                                                    Document Name
                                                </Typography>
                                                <Typography variant="body2" noWrap sx={{ color: ht.text, fontWeight: 500 }}>
                                                    {documentDetails.name || 'N/A'}
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                                                    Document Type
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: ht.text }}>
                                                    {documentDetails.documentTypeName ||
                                                        documentDetails.documentTypeId ||
                                                        'N/A'}
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                                                    Uploaded At
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: ht.text }}>
                                                    {documentDetails.uploadAt || documentDetails.createdAt
                                                        ? formatDateTime(
                                                              documentDetails.uploadAt || documentDetails.createdAt,
                                                              dateTimeFormat
                                                          )
                                                        : 'N/A'}
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                                                    Location
                                                </Typography>
                                                <Tooltip title={documentDetails.location || 'N/A'} arrow placement="top">
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        sx={{
                                                            maxWidth: '300px',
                                                            textOverflow: 'ellipsis',
                                                            overflow: 'hidden',
                                                            display: 'inline-block',
                                                            verticalAlign: 'bottom',
                                                            fontSize: '0.875rem',
                                                            color: ht.text,
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                                textDecorationColor: ht.text,
                                                                textDecorationThickness: '1px',
                                                                textUnderlineOffset: '2px',
                                                            },
                                                        }}
                                                    >
                                                        {documentDetails.location || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                            </div>
                                            <div>
                                                <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                                                    Unit of Work
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: ht.text }}>
                                                    {documentDetails.unitOfWorkId || 'N/A'}
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography variant="caption" display="block" sx={{ color: ht.text }}>
                                                    Integration ID
                                                </Typography>
                                                <Typography variant="body2" noWrap sx={{ color: ht.text }}>
                                                    {documentDetails.integrationId != null
                                                        ? String(documentDetails.integrationId)
                                                        : 'N/A'}
                                                </Typography>
                                            </div>
                                        </Box>
                                    </Box>
                                )}

                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: ht.white,
                                        borderRadius: ht.cardRadius,
                                        border: `1px solid ${ht.line}`,
                                        boxShadow: ht.cardShadow,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 1.5,
                                            pb: 1,
                                            borderBottom: `1px solid ${ht.line}`,
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                                            AUDIT LOGS
                                        </Typography>
                                    </Box>
                                    {auditLogs.length > 0 ? (
                                        <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                                            <DataGridPro
                                                rows={auditLogs}
                                                pagination
                                                paginationModel={auditLogsPaginationModel}
                                                onPaginationModelChange={setAuditLogsPaginationModel}
                                                pageSizeOptions={[10, 25, 50]}
                                                columns={[
                                                    {
                                                        field: 'createdAt',
                                                        headerName: 'Timestamp',
                                                        flex: 1,
                                                        minWidth: 160,
                                                        resizable: true,
                                                        valueFormatter: (params) =>
                                                            params?.value ? new Date(params.value).toLocaleString() : 'N/A',
                                                        renderHeader: () => (
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                                                Timestamp
                                                            </Typography>
                                                        ),
                                                    },
                                                    {
                                                        field: 'service',
                                                        headerName: 'Service',
                                                        flex: 0.8,
                                                        minWidth: 150,
                                                        resizable: true,
                                                        cellClassName: 'service-cell',
                                                        renderHeader: () => (
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                                                Service
                                                            </Typography>
                                                        ),
                                                        renderCell: (params) => (
                                                            <Box
                                                                sx={{
                                                                    bgcolor:
                                                                        params.value === 'upload'
                                                                            ? ht.secondaryTab
                                                                            : params.value === 'document_classification'
                                                                              ? 'rgba(2, 107, 29, 0.12)'
                                                                              : params.value === 'extraction'
                                                                                ? 'rgba(46, 166, 160, 0.18)'
                                                                                : params.value === 'ingest'
                                                                                  ? 'rgba(1, 48, 98, 0.12)'
                                                                                  : ht.listBg,
                                                                    color:
                                                                        params.value === 'upload'
                                                                            ? ht.text
                                                                            : params.value === 'document_classification'
                                                                              ? ht.green
                                                                              : params.value === 'extraction'
                                                                                ? ht.blue
                                                                                : params.value === 'ingest'
                                                                                  ? ht.text
                                                                                  : ht.text,
                                                                    px: 1.5,
                                                                    py: 0.5,
                                                                    borderRadius: ht.buttonRadius,
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 500,
                                                                    textTransform: 'none',
                                                                    minWidth: 'fit-content',
                                                                    width: 'auto',
                                                                    whiteSpace: 'nowrap',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                {params.value || 'N/A'}
                                                            </Box>
                                                        ),
                                                    },
                                                    {
                                                        field: 'action',
                                                        headerName: 'Action',
                                                        flex: 1,
                                                        minWidth: 120,
                                                        resizable: true,
                                                        renderHeader: () => (
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                                                Action
                                                            </Typography>
                                                        ),
                                                        valueFormatter: (params) => params?.value || 'N/A',
                                                        renderCell: (params) => (
                                                            <Box
                                                                sx={{
                                                                    color: ht.text,
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 500,
                                                                    textTransform: 'none',
                                                                    fontFamily: 'monospace',
                                                                }}
                                                            >
                                                                {params.formattedValue != null && params.formattedValue !== ''
                                                                    ? params.formattedValue
                                                                    : params.value || 'N/A'}
                                                            </Box>
                                                        ),
                                                    },
                                                    {
                                                        field: 'status',
                                                        headerName: 'Status',
                                                        flex: 0.8,
                                                        minWidth: 100,
                                                        resizable: true,
                                                        renderHeader: () => (
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                                                Status
                                                            </Typography>
                                                        ),
                                                        renderCell: (params) => (
                                                            <Box
                                                                sx={{
                                                                    color: ht.text,
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 500,
                                                                    textTransform: 'uppercase',
                                                                }}
                                                            >
                                                                {params.value || 'N/A'}
                                                            </Box>
                                                        ),
                                                    },
                                                    {
                                                        field: 'details',
                                                        headerName: 'Details',
                                                        flex: 2,
                                                        minWidth: 200,
                                                        resizable: true,
                                                        renderHeader: () => (
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                                                Details
                                                            </Typography>
                                                        ),
                                                        renderCell: (params) => {
                                                            const displayText =
                                                                params.value == null
                                                                    ? ''
                                                                    : typeof params.value === 'object'
                                                                      ? JSON.stringify(params.value)
                                                                      : String(params.value);
                                                            const preview =
                                                                displayText.length > 0
                                                                    ? `${displayText.slice(0, 50)}${displayText.length > 50 ? '...' : ''}`
                                                                    : 'N/A';
                                                            return (
                                                                <Tooltip
                                                                    title={
                                                                        params.value
                                                                            ? typeof params.value === 'object'
                                                                              ? JSON.stringify(params.value, null, 2)
                                                                              : String(params.value)
                                                                            : 'N/A'
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: '100%',
                                                                            '&:hover': {
                                                                                cursor: 'pointer',
                                                                                textDecoration: 'underline',
                                                                                textDecorationColor: ht.text,
                                                                                textDecorationThickness: '1px',
                                                                                textUnderlineOffset: '2px',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            noWrap
                                                                            sx={{
                                                                                color: ht.text,
                                                                                fontSize: '0.8rem',
                                                                                fontFamily: 'monospace',
                                                                            }}
                                                                        >
                                                                            {preview}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            );
                                                        },
                                                    },
                                                ]}
                                                getRowId={(row) =>
                                                    row?.id != null
                                                        ? String(row.id)
                                                        : `${row?.createdAt ?? ''}_${row?.service ?? ''}_${row?.action ?? ''}_${row?.status ?? ''}`
                                                }
                                                disableRowSelectionOnClick
                                                disableColumnMenu
                                                hideFooterSelectedRowCount
                                                sx={{
                                                    ...harmonateDataGridSx,
                                                    border: 'none',
                                                    '& .MuiDataGrid-columnHeaders': {
                                                        backgroundColor: ht.listBg,
                                                        borderBottom: `1px solid ${ht.line}`,
                                                        minHeight: '40px !important',
                                                        maxHeight: '40px !important',
                                                    },
                                                    '& .MuiDataGrid-columnHeader': {
                                                        py: 1,
                                                    },
                                                    '& .MuiDataGrid-columnHeaderTitle': {
                                                        fontWeight: 600,
                                                        color: ht.text,
                                                    },
                                                    '& .MuiDataGrid-cell': {
                                                        fontSize: '0.8125rem',
                                                        color: ht.text,
                                                        py: 1,
                                                        borderColor: ht.line,
                                                    },
                                                    '& .MuiDataGrid-row': {
                                                        '&:hover': {
                                                            backgroundColor: ht.secondaryTab,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: ht.secondaryTab,
                                                            '&:hover': {
                                                                backgroundColor: ht.secondaryTab,
                                                            },
                                                        },
                                                    },
                                                    '& .MuiDataGrid-footerContainer': {
                                                        borderTop: `1px solid ${ht.line}`,
                                                        minHeight: '48px',
                                                    },
                                                }}
                                                components={{
                                                    LoadingOverlay: LinearProgress,
                                                    NoRowsOverlay: () => (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                height: '100%',
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ color: ht.text }}>
                                                                No audit logs available
                                                            </Typography>
                                                        </Box>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography sx={{ color: ht.text }}>No audit logs available</Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Box>
            )}

            <Dialog
                open={logsExtractionDialogOpen}
                onClose={() => setLogsExtractionDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={harmonateDialogPaperProps}
            >
                <DialogTitle sx={harmonateDialogTitleSx}>
                    <Typography variant="h6" sx={{ color: ht.white, fontWeight: 700 }}>Extraction Details</Typography>
                </DialogTitle>
                <DialogContent sx={harmonateDialogContentSx}>
                    {logsExtractionLoading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress sx={{ color: ht.blue }} />
                        </Box>
                    ) : logsExtractionDetails ? (
                        <pre
                            style={{
                                backgroundColor: ht.listBg,
                                padding: '16px',
                                borderRadius: '4px',
                                maxHeight: '60vh',
                                overflow: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                margin: 0,
                            }}
                        >
                            {JSON.stringify(logsExtractionDetails, null, 2)}
                        </pre>
                    ) : (
                        <Box p={3} textAlign="center">
                            <Typography color="textSecondary">No extraction data available</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${ht.line}` }}>
                    <Button
                        onClick={() => setLogsExtractionDialogOpen(false)}
                        variant="contained"
                        sx={harmonateProceedButtonSx}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={logsJsonDialogOpen} onClose={() => setLogsJsonDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
                <DialogTitle sx={harmonateDialogTitleSx}>File Content</DialogTitle>
                <DialogContent sx={harmonateDialogContentSx}>
                    {logsJsonLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress sx={{ color: ht.blue }} />
                        </Box>
                    ) : logsJsonError ? (
                        <Alert severity="error">
                            <AlertTitle>Error</AlertTitle>
                            {logsJsonError}
                        </Alert>
                    ) : logsJsonData && logsJsonData.isBinary ? (
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Binary File Detected
                            </Typography>
                            <Typography variant="body1" paragraph>
                                This is a binary file and cannot be displayed directly.
                            </Typography>
                            <Button
                                variant="contained"
                                component="a"
                                href={logsJsonData.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ ...harmonateProceedButtonSx, mt: 2 }}
                            >
                                Download File
                            </Button>
                        </Box>
                    ) : (
                        <pre
                            style={{
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                fontFamily: 'monospace',
                                maxHeight: '60vh',
                                overflow: 'auto',
                                padding: '16px',
                                backgroundColor: ht.listBg,
                                borderRadius: '4px',
                            }}
                        >
                            {logsJsonData?.content || 'No data available'}
                        </pre>
                    )}
                </DialogContent>
                <DialogActions>
                    {logsJsonData?.downloadUrl && (
                        <Button
                            component="a"
                            href={logsJsonData.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={harmonateCancelButtonSx}
                        >
                            Download
                        </Button>
                    )}
                    <Button onClick={() => setLogsJsonDialogOpen(false)} sx={harmonateCancelButtonSx}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* ✅ 3-dot Menu for Recent Uploads */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                        minWidth: 220,
                        maxWidth: 320,
                    }
                }}
            >
                <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                        Surrogate key
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-word' }}>
                        {selectedUploadRow?.surrogateKey && selectedUploadRow.surrogateKey !== '—'
                            ? selectedUploadRow.surrogateKey
                            : '—'}
                    </Typography>
                </Box>
                <Divider />
                <MenuItem
                    onClick={handleView}
                    disabled={!selectedUploadRow?.unitOfWorkId || !selectedUploadRow?.integrationId}
                >
                    <Typography variant="body2">View Metadata</Typography>
                </MenuItem>
                <MenuItem
                    onClick={handleViewData}
                    disabled={!selectedUploadRow?.unitOfWorkId || !selectedUploadRow?.integrationId}
                >
                    <Typography variant="body2">View Data</Typography>
                </MenuItem>

                {/* <MenuItem
                    onClick={handleDeleteUpload}
                    disabled={
                        !selectedUploadRow?.fileInstanceId ||
                        (!isHarmonateUser && !hasPermission('GL Sync', 'delete'))
                    }
                    sx={{
                        '&.Mui-disabled': {
                            opacity: 0.5,
                            pointerEvents: 'none'
                        }
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: (!isHarmonateUser && !hasPermission('GL Sync', 'delete')) ? 'rgba(0, 0, 0, 0.38)' : '#f44336',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        Delete
                    </Typography>
                </MenuItem> */}
            </Menu>

            {/* Excel Viewer Dialog */}
            <ExcelViewerDialog
                open={viewerOpen}
                onClose={handleCloseViewer}
                data={viewerData}
                loading={viewerLoading}
                error={error}
                fileName={selectedUploadRow?.name}
            />

            {/* View Data Dialog */}
            <ViewDataDialog
                open={viewDataOpen}
                onClose={handleCloseViewData}
                data={viewDataData}
                loading={viewDataLoading}
                error={error}
                fileName={selectedUploadRow?.name}
            />

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={cancelDelete}
                maxWidth="sm"
                fullWidth
                PaperProps={harmonateDialogPaperProps}
            >
                <DialogTitle sx={harmonateDialogTitleSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteIcon sx={{ color: ht.white, fontSize: '24px' }} />
                        <Typography variant="h6" component="div" sx={{ color: ht.white, fontWeight: 700 }}>
                            Confirm Delete
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={harmonateDialogContentSx}>
                    <Typography variant="body1">
                        Are you sure you want to delete the upload record "{itemToDelete?.name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={cancelDelete}
                        variant="outlined"
                        sx={harmonateCancelButtonSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        sx={harmonateAbortButtonSx}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Confirmation Dialog for Single Account Delete */}
            <Dialog
                open={deleteAccountDialogOpen}
                onClose={cancelDeleteAccount}
                maxWidth="sm"
                fullWidth
                PaperProps={harmonateDialogPaperProps}
            >
                <DialogTitle sx={harmonateDialogTitleSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteIcon sx={{ color: ht.white, fontSize: '24px' }} />
                        <Typography variant="h6" component="div" sx={{ color: ht.white, fontWeight: 700 }}>
                            Confirm Delete
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={harmonateDialogContentSx}>
                    <Typography variant="body1">
                        Are you sure you want to delete the account "{accountToDelete?.glCode} - {accountToDelete?.glDescription}"? This action cannot be undone.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={cancelDeleteAccount}
                        variant="outlined"
                        sx={harmonateCancelButtonSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteAccount}
                        variant="contained"
                        sx={harmonateAbortButtonSx}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog for All Accounts Delete */}
            <Dialog
                open={deleteAllDialogOpen}
                onClose={cancelDeleteAll}
                maxWidth="sm"
                fullWidth
                PaperProps={harmonateDialogPaperProps}
            >
                <DialogTitle sx={harmonateDialogTitleSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteIcon sx={{ color: ht.white, fontSize: '24px' }} />
                        <Typography variant="h6" component="div" sx={{ color: ht.white, fontWeight: 700 }}>
                            Confirm Delete All
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={harmonateDialogContentSx}>
                    <Typography variant="body1">
                        Are you sure you want to delete all accounts for {selectedSystem}? This action cannot be undone.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={cancelDeleteAll}
                        variant="outlined"
                        sx={harmonateCancelButtonSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteAll}
                        variant="contained"
                        sx={harmonateAbortButtonSx}
                    >
                        Delete All
                    </Button>
                </DialogActions>
            </Dialog>

            <RefTemplateMappingsEditorDrawer
                open={gaTemplateMappingsEditorOpen}
                onClose={() => setGaTemplateMappingsEditorOpen(false)}
                apiTemplate={activeGaApiTemplateForEditor}
                getAuthToken={getUploadAuthToken}
                persistToApi={false}
                onLocalMappingChange={(templateId, mapping) => {
                    setLocalGaRefTemplateMappingById((prev) => ({ ...prev, [templateId]: mapping }));
                }}
            />

            <Dialog
                open={Boolean(coaConfirmAction)}
                onClose={() => !(refreshingCoa || pushingCoa) && setCoaConfirmAction(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={harmonateDialogPaperProps}
            >
                <DialogTitle sx={harmonateDialogTitleSx}>
                    {coaConfirmAction === 'push' ? 'Push COA to Snowflake?' : 'Refresh COA from Snowflake?'}
                </DialogTitle>
                <DialogContent sx={harmonateDialogContentSx}>
                    <DialogContentText>
                        {coaConfirmAction === 'push'
                            ? 'This will push GL Sync chart of accounts from Postgres into Snowflake (DEV_ADMIN.POSTGRES.CHART_OF_ACCOUNT). Existing matching rows will be updated.'
                            : 'This will load chart of accounts from Snowflake into Postgres (system_type GL_SYNC). Existing matching rows will be updated.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setCoaConfirmAction(null)}
                        disabled={refreshingCoa || pushingCoa}
                        sx={harmonateCancelButtonSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => (coaConfirmAction === 'push' ? handlePushCoa() : handleRefreshCoa())}
                        disabled={refreshingCoa || pushingCoa}
                        startIcon={
                            refreshingCoa || pushingCoa ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : coaConfirmAction === 'push' ? (
                                <CloudUploadIcon />
                            ) : (
                                <CloudDownloadIcon />
                            )
                        }
                        sx={harmonateProceedButtonSx}
                    >
                        {refreshingCoa || pushingCoa
                            ? coaConfirmAction === 'push'
                                ? 'Pushing…'
                                : 'Refreshing…'
                            : coaConfirmAction === 'push'
                              ? 'Push COA'
                              : 'Refresh COA'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GLAccounts;

